/**
 * File: assets/basari-contact-lens.js
 * Purpose: Contact Lens PDP — presentation interactions + Recharge plan sync UI.
 * Sprint: Subscribe Lenses Modal + Recharge plan discovery
 * Owner: Basari Theme / PDP Contact Lens
 * Dependencies: [data-basari-contact-lens], [data-basari-cl-config], Recharge widget radios
 * Notes:
 *   - UI interactions (pack, power, coupon, Subscribe Lenses modal).
 *   - Subscribe modal discovers Recharge rc_plan radios dynamically (no hardcoded IDs).
 *   - Proceed to pay submits the existing /cart/add form via requestSubmit().
 *   - The cart icon reuses that validation, quantity, and line-item properties,
 *     then adds via PremiumCart.add() (one /cart/add.js request) and opens the
 *     Premium Cart Drawer. Falls back to the Dawn drawer only if the premium
 *     section is missing.
 *   - Colour variant sync is owned by basari-product-variants.js (shared).
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-basari-contact-lens]';
  var READY_ATTR = 'data-basari-cl-ready';

  /**
   * Extension registry — assign functions in future sprints.
   * Preserve all hooks; add aliases without removing legacy names.
   */
  var extensions = {
    loadSphOptions: null,
    loadRxOptions: null,
    updatePricing: null,
    validate: null,
    buildLineItemProperties: null,
    addToCart: null,
    buyNow: null,
    openSubscribeModal: null,
    checkDeliveryEligibility: null,
    lookupDeliveryZip: null,
    fetchDeliveryEstimate: null,
    loadPacks: null,
    track: null,
    analytics: null
  };

  function parseConfig(root) {
    var node = root.querySelector('[data-basari-cl-config]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return {};
    }
  }

  function dispatch(root, name, detail) {
    var event;
    try {
      event = new CustomEvent(name, {
        bubbles: true,
        detail: detail || {}
      });
    } catch (error) {
      event = document.createEvent('CustomEvent');
      event.initCustomEvent(name, true, true, detail || {});
    }
    root.dispatchEvent(event);
  }

  function cacheDom(root) {
    return {
      form: root.querySelector('[data-basari-cl-form]'),
      pack: root.querySelector('[data-basari-cl-pack]'),
      packCards: root.querySelectorAll('[data-basari-cl-pack-card]'),
      packInput: root.querySelector('[data-basari-cl-pack-input]'),
      packApiMount: root.querySelector('[data-basari-cl-pack-api-mount]'),
      power: root.querySelector('[data-basari-cl-power]'),
      powerTabs: root.querySelectorAll('[data-basari-cl-power-tab]'),
      powerPanel: root.querySelector('[data-basari-cl-power-panel]'),
      powerTypeInput: root.querySelector('[data-basari-cl-power-type-input]'),
      eyeInputs: root.querySelectorAll('[data-basari-cl-power-eye-input]'),
      warning: root.querySelector('[data-basari-cl-power-warning]'),
      rxFieldInputs: root.querySelectorAll('[data-basari-cl-rx-field-input]'),
      sphSelects: root.querySelectorAll('[data-basari-cl-power-sph], .js-basari-cl-power-sph'),
      boxesSelects: root.querySelectorAll('[data-basari-cl-power-boxes], .js-basari-cl-power-boxes'),
      sphTriggers: root.querySelectorAll('[data-basari-cl-sph-trigger]'),
      sphModal: root.querySelector('[data-basari-cl-sph-modal]'),
      sphRows: root.querySelectorAll('[data-basari-cl-power-row="sph"]'),
      rxTriggers: root.querySelectorAll('[data-basari-cl-rx-trigger]'),
      rxModals: root.querySelectorAll('[data-basari-cl-rx-modal]'),
      quantityInput: root.querySelector('[data-basari-cl-quantity]'),
      purchase: root.querySelector('[data-basari-cl-purchase]'),
      buyNow: root.querySelector('[data-basari-cl-buy-now]'),
      addToCart: root.querySelector('[data-basari-add-cart]'),
      subscribeOpen: root.querySelector('[data-basari-cl-subscribe-open]'),
      subscribeMount: root.querySelector('[data-basari-cl-subscribe-modal-mount]'),
      expressCheckout: root.querySelector('[data-basari-cl-express-checkout]'),
      thirdPartyActions: root.querySelector('[data-basari-cl-third-party-actions]'),
      deliveryModule: root.querySelector('[data-basari-delivery-module]'),
      deliveryBadge: root.querySelector('[data-basari-delivery-eligibility]'),
      deliveryZip: root.querySelector('[data-basari-delivery-zip-input]'),
      deliveryEstimate: root.querySelector('[data-basari-delivery-estimate]'),
      variantInput: root.querySelector('[data-basari-cl-variant-id]'),
      coupon: root.querySelector('[data-basari-coupon]'),
      couponCopy: root.querySelector('[data-basari-coupon-copy]')
    };
  }

  /**
   * Power mode visibility is data-driven via tab flags:
   * data-hides-panel — hide the entire RX panel
   * data-hides-sph — hide SPH rows only (eyes + boxes stay visible)
   */
  function setPowerMode(instance, mode) {
    if (!instance.dom.power || !mode) return;
    var next = String(mode);
    instance.dom.power.setAttribute('data-power-mode', next);

    var hidesPanel = false;
    var hidesSph = false;
    var i;
    for (i = 0; i < instance.dom.powerTabs.length; i += 1) {
      var tab = instance.dom.powerTabs[i];
      var active = tab.getAttribute('data-power-mode') === next;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.classList.toggle('basari-cl-power__tab--active', active);
      if (active && tab.getAttribute('data-hides-panel') === 'true') {
        hidesPanel = true;
      }
      if (active && tab.getAttribute('data-hides-sph') === 'true') {
        hidesSph = true;
      }
    }

    if (instance.dom.powerPanel) {
      if (hidesPanel) {
        instance.dom.powerPanel.setAttribute('hidden', '');
      } else {
        instance.dom.powerPanel.removeAttribute('hidden');
      }
    }

    instance.dom.power.setAttribute('data-hide-sph', hidesSph ? 'true' : 'false');
    syncSphRowVisibility(instance, hidesSph);
    if (hidesSph) {
      closeSphModal(instance);
      clearAllSphErrors(instance);
    }

    if (instance.dom.powerTypeInput) {
      instance.dom.powerTypeInput.value = next;
    }

    syncEyeWarning(instance);
    updateEyeFieldState(instance);

    dispatch(instance.root, 'basari:cl:power-mode', {
      mode: next,
      hidesPanel: hidesPanel,
      hidesSph: hidesSph,
      sectionId: instance.config.sectionId
    });
  }

  function setEyeVisibility(instance, eye, enabled) {
    if (!eye) return;
    var cols =
      instance.root && instance.root.querySelectorAll
        ? instance.root.querySelectorAll('[data-basari-cl-power-col="' + eye + '"]')
        : [];
    var i;
    for (i = 0; i < cols.length; i += 1) {
      if (enabled) {
        cols[i].removeAttribute('hidden');
      } else {
        cols[i].setAttribute('hidden', '');
      }
    }

    syncEyeWarning(instance);
    updateEyeFieldState(instance);
    if (!enabled) {
      clearSphError(instance, eye);
      if (isToricLens(instance)) {
        clearFieldError(instance, 'cyl', eye);
        clearFieldError(instance, 'axis', eye);
      }
      if (isMultifocalLens(instance)) {
        clearFieldError(instance, 'add', eye);
      }
    }

    dispatch(instance.root, 'basari:cl:eye-toggle', {
      eye: eye,
      enabled: enabled,
      sectionId: instance.config.sectionId
    });
  }

  /**
   * Last-eye lock makes this a safety fallback only.
   */
  function syncEyeWarning(instance) {
    if (!instance.dom.warning || !instance.dom.power) return;
    if (isSpecialLens(instance)) {
      if (isSpecialEyeActive(instance, 'right') || isSpecialEyeActive(instance, 'left')) {
        instance.dom.warning.setAttribute('hidden', '');
      }
      return;
    }

    var panelHidden =
      instance.dom.powerPanel && instance.dom.powerPanel.hasAttribute('hidden');
    var anyChecked = countCheckedEyes(instance) > 0;

    if (!panelHidden && !anyChecked) {
      instance.dom.warning.removeAttribute('hidden');
    } else {
      instance.dom.warning.setAttribute('hidden', '');
    }
  }

  function selectPack(instance, card) {
    if (!card) return;
    var value = card.getAttribute('data-pack-value') || '';
    var i;
    for (i = 0; i < instance.dom.packCards.length; i += 1) {
      var node = instance.dom.packCards[i];
      var selected = node === card;
      node.setAttribute('data-selected', selected ? 'true' : 'false');
      node.setAttribute('aria-checked', selected ? 'true' : 'false');
      node.classList.toggle('basari-cl-pack__card--selected', selected);
    }
    if (instance.dom.packInput) {
      instance.dom.packInput.value = value;
    }
    dispatch(instance.root, 'basari:cl:pack-select', {
      value: value,
      source: card.getAttribute('data-pack-source'),
      sectionId: instance.config.sectionId
    });
    if (typeof extensions.updatePricing === 'function') {
      extensions.updatePricing(instance);
    }
    var openModal = getSubscribeModal(instance);
    if (openModal && !openModal.hasAttribute('hidden')) {
      syncSubscribeProductState(instance, openModal);
      syncSubscribeSummary(instance, openModal);
    }
  }

  function copyCoupon(instance) {
    var coupon = instance.dom.coupon;
    var button = instance.dom.couponCopy;
    if (!coupon || !button) return;

    var codeNode = coupon.querySelector('[data-basari-coupon-code-value]');
    var code =
      coupon.getAttribute('data-coupon-code') ||
      (codeNode ? codeNode.textContent : '');
    code = String(code || '').trim();
    if (!code) return;

    var onCopied = function () {
      button.setAttribute('data-copied', 'true');
      dispatch(instance.root, 'basari:cl:coupon-copy', {
        code: code,
        sectionId: instance.config.sectionId
      });
      window.setTimeout(function () {
        button.removeAttribute('data-copied');
      }, 1600);
    };

    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(code).then(onCopied).catch(function () {
        fallbackCopy(code, onCopied);
      });
      return;
    }

    fallbackCopy(code, onCopied);
  }

  function fallbackCopy(text, onCopied) {
    var area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'absolute';
    area.style.left = '-9999px';
    document.body.appendChild(area);
    area.select();
    try {
      document.execCommand('copy');
      if (typeof onCopied === 'function') onCopied();
    } catch (error) {
      /* no-op — presentation helper only */
    }
    document.body.removeChild(area);
  }

  /**
   * Subscribe Lenses modal — presentation layer over Recharge plan radios.
   * Discovers real Recharge plans from the DOM (incl. Shadow DOM). Does not
   * invent plan IDs or duplicate Recharge subscription logic.
   *
   * Event: basari:cl:subscribe-proceed
   * Payload:
   *   sectionId, productId, variantId, packValue, packLabel,
   *   frequency, duration, planLabel, quantity, unitPriceCents,
   *   compareAtPriceCents, totalCents, currency, sellingPlanId
   * No theme code listens to this event. `quantity` is a legacy alias of
   * intervalCount (not cart line quantity). `totalCents` is unused/legacy
   * and is not a cart total. Proceed then submits /cart/add via requestSubmit()
   * so Shopify / Recharge submit listeners still run.
   */
  var BODY_OPEN_CLASS = 'basari-cl-subscribe-modal-open';
  var FREQ_SHOW_BANNER = { daily: true, weekly: true, monthly: false };
  var FREQ_ORDER = ['daily', 'weekly', 'monthly'];
  var RC_PLAN_SELECTOR = 'input[type="radio"][name="rc_plan"]';
  var RC_HOST_SELECTOR = [
    'recharge-subscription-widget',
    '[data-recharge-subscription-widget]',
    '[data-widget-type="subscription"]',
    '.rc-widget',
    '#rc_container',
    '[id*="recharge"]',
    '[class*="recharge"]'
  ].join(',');
  var RECHARGE_WAIT_MS = 80;
  var RECHARGE_WAIT_MAX = 40;
  var RECHARGE_MUTATION_DEBOUNCE_MS = 100;

  function parseJsonScript(node) {
    if (!node) return {};
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return {};
    }
  }

  function formatMoneyCents(cents, currency) {
    var amount = (Number(cents) || 0) / 100;
    var code = currency || '';
    if (!code) {
      return amount.toFixed(2);
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      return String(amount.toFixed(2));
    }
  }

  function getFocusable(container) {
    if (!container) return [];
    var nodes = container.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    var list = [];
    var i;
    for (i = 0; i < nodes.length; i += 1) {
      if (nodes[i].closest('[hidden]')) continue;
      if (nodes[i].getAttribute('tabindex') === '-1') continue;
      list.push(nodes[i]);
    }
    return list;
  }

  function normalizeWhitespace(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function canonicalizePlanLabel(label) {
    var normalized = normalizeWhitespace(label);
    var match = normalized.match(/^(\d+)\s*(days?|weeks?|months?)$/);
    if (!match) return normalized;
    var count = parseInt(match[1], 10);
    var unit = match[2].replace(/s$/, '');
    if (!count || count < 1) return normalized;
    if (count === 1) return '1 ' + unit;
    return count + ' ' + unit + 's';
  }

  function getPlanTypeFromLabel(label) {
    var normalized = canonicalizePlanLabel(label);
    if (/\bmonths?\b/.test(normalized)) return 'monthly';
    if (/\bweeks?\b/.test(normalized)) return 'weekly';
    if (/\bdays?\b/.test(normalized)) return 'daily';
    return null;
  }

  function getPlanCountFromLabel(label) {
    var match = canonicalizePlanLabel(label).match(/^(\d+)\s/);
    if (!match) return 1;
    var count = parseInt(match[1], 10);
    return count && count >= 1 ? count : 1;
  }

  function formatPlanDisplayLabel(label) {
    var canonical = canonicalizePlanLabel(label);
    if (!canonical) return '';
    return canonical.replace(/\b\w/g, function (char) {
      return char.toUpperCase();
    });
  }

  function querySelectorAllDeep(root, selector) {
    var results = [];
    var seen = typeof WeakSet === 'function' ? new WeakSet() : null;

    function collect(node) {
      if (!node || (seen && seen.has(node))) return;
      if (seen) seen.add(node);

      var list = null;
      try {
        if (node.querySelectorAll) list = node.querySelectorAll(selector);
      } catch (error) {
        list = null;
      }
      if (list) {
        var i;
        for (i = 0; i < list.length; i += 1) {
          results.push(list[i]);
        }
      }

      var children = null;
      try {
        if (node.querySelectorAll) children = node.querySelectorAll('*');
      } catch (error) {
        children = null;
      }
      if (!children) return;
      var j;
      for (j = 0; j < children.length; j += 1) {
        if (children[j].shadowRoot) collect(children[j].shadowRoot);
      }
    }

    collect(root || document);
    return results;
  }

  function readRechargePlanLabel(input) {
    if (!input) return '';
    var aria = input.getAttribute('aria-label');
    if (aria && String(aria).trim()) return String(aria).trim();

    var id = input.id;
    if (id) {
      try {
        var byFor = document.querySelector('label[for="' + id.replace(/"/g, '\\"') + '"]');
        if (byFor) {
          var forText = byFor.textContent;
          if (forText && String(forText).trim()) return String(forText).trim();
        }
      } catch (error) {
        /* ignore invalid selector */
      }
    }

    var parentLabel = input.closest ? input.closest('label') : null;
    if (parentLabel) {
      var clone = parentLabel.cloneNode(true);
      var nestedInputs = clone.querySelectorAll('input, select, textarea, button');
      var i;
      for (i = 0; i < nestedInputs.length; i += 1) {
        if (nestedInputs[i].parentNode) nestedInputs[i].parentNode.removeChild(nestedInputs[i]);
      }
      var labelText = clone.textContent;
      if (labelText && String(labelText).trim()) return String(labelText).trim();
    }

    var sibling = input.nextElementSibling;
    if (sibling && sibling.textContent && String(sibling.textContent).trim()) {
      return String(sibling.textContent).trim();
    }

    return '';
  }

  function isElementVisibleEnough(node) {
    if (!node || !node.getAttribute) return false;
    if (node.disabled) return false;
    if (node.closest && node.closest('[hidden]')) return false;
    var style = null;
    try {
      style = window.getComputedStyle ? window.getComputedStyle(node) : null;
    } catch (error) {
      style = null;
    }
    if (style && (style.display === 'none' || style.visibility === 'hidden')) return false;
    return true;
  }

  function collectRechargePlanInputs(root) {
    return querySelectorAllDeep(root || document, RC_PLAN_SELECTOR);
  }

  function buildRechargeSearchRoots(preferredRoot) {
    var roots = [];
    var seen = typeof WeakSet === 'function' ? new WeakSet() : null;

    function pushRoot(node) {
      if (!node) return;
      if (seen) {
        if (seen.has(node)) return;
        seen.add(node);
      } else if (roots.indexOf(node) !== -1) {
        return;
      }
      roots.push(node);
    }

    pushRoot(preferredRoot || null);

    if (preferredRoot && preferredRoot.querySelector) {
      pushRoot(preferredRoot.querySelector('[data-basari-cl-form]'));
      pushRoot(preferredRoot.querySelector('form[action*="/cart/add"]'));
      pushRoot(preferredRoot.querySelector('form'));
    }

    if (preferredRoot && preferredRoot.closest) {
      pushRoot(preferredRoot.closest('form'));
      pushRoot(preferredRoot.closest('[data-basari-contact-lens]'));
      pushRoot(preferredRoot.closest('.product'));
      pushRoot(preferredRoot.closest('product-info'));
    }

    try {
      var hosts = document.querySelectorAll(RC_HOST_SELECTOR);
      var h;
      for (h = 0; h < hosts.length; h += 1) {
        pushRoot(hosts[h]);
      }
    } catch (error) {
      /* selector support varies; fall through to document */
    }

    pushRoot(document);
    return roots;
  }

  function scoreRechargeInput(input, preferredRoot) {
    var score = 0;
    if (input.checked) score += 8;
    if (isElementVisibleEnough(input)) score += 4;
    if (preferredRoot && preferredRoot.contains && preferredRoot.contains(input)) score += 6;
    return score;
  }

  function buildPlansFromInputs(inputs, preferredRoot) {
    var bestByKey = {};
    var i;

    for (i = 0; i < inputs.length; i += 1) {
      var input = inputs[i];
      var label = readRechargePlanLabel(input);
      var normalizedLabel = canonicalizePlanLabel(label);
      var type = getPlanTypeFromLabel(normalizedLabel);
      if (!normalizedLabel || !type) continue;

      var dedupeKey = type + '::' + normalizedLabel;
      var candidate = {
        label: label,
        normalizedLabel: normalizedLabel,
        displayLabel: formatPlanDisplayLabel(normalizedLabel),
        type: type,
        count: getPlanCountFromLabel(normalizedLabel),
        input: input,
        checked: !!input.checked,
        score: scoreRechargeInput(input, preferredRoot)
      };

      if (!bestByKey[dedupeKey] || candidate.score > bestByKey[dedupeKey].score) {
        bestByKey[dedupeKey] = candidate;
      }
    }

    var plans = [];
    var key;
    for (key in bestByKey) {
      if (Object.prototype.hasOwnProperty.call(bestByKey, key)) {
        plans.push(bestByKey[key]);
      }
    }

    plans.sort(function (a, b) {
      if (a.type === b.type) return a.count - b.count;
      return FREQ_ORDER.indexOf(a.type) - FREQ_ORDER.indexOf(b.type);
    });

    return plans;
  }

  /**
   * Discover Recharge plan radios dynamically (no hardcoded plan IDs).
   * Prefers product/widget-scoped roots before falling back to document-wide search.
   */
  function discoverRechargePlans(preferredRoot) {
    var roots = buildRechargeSearchRoots(preferredRoot);
    var r;
    for (r = 0; r < roots.length; r += 1) {
      var root = roots[r];
      if (root === document) continue;
      var scoped = buildPlansFromInputs(collectRechargePlanInputs(root), preferredRoot);
      if (scoped.length) return scoped;
    }
    return buildPlansFromInputs(collectRechargePlanInputs(document), preferredRoot);
  }

  function getSubscribeDiscoveryRoot(instance, modal) {
    if (instance && instance.root) return instance.root;
    if (modal && modal.closest) {
      return (
        modal.closest('[data-basari-contact-lens]') ||
        modal.closest('form') ||
        document
      );
    }
    return document;
  }

  function groupRechargePlans(plans) {
    var groups = { daily: [], weekly: [], monthly: [] };
    var i;
    for (i = 0; i < plans.length; i += 1) {
      var plan = plans[i];
      if (groups[plan.type]) groups[plan.type].push(plan);
    }
    return groups;
  }

  function getCheckedRechargePlan(plans) {
    var i;
    for (i = 0; i < plans.length; i += 1) {
      if (plans[i].input && plans[i].input.checked) return plans[i];
    }
    return null;
  }

  function findPlanByNormalized(plans, normalizedLabel) {
    var key = canonicalizePlanLabel(normalizedLabel);
    var i;
    for (i = 0; i < plans.length; i += 1) {
      if (plans[i].normalizedLabel === key) return plans[i];
    }
    return null;
  }

  function activateRechargePlan(plan) {
    if (!plan || !plan.input) return false;
    var input = plan.input;
    var alreadyChecked = !!input.checked;

    try {
      if (!alreadyChecked && typeof input.click === 'function') {
        input.click();
      } else if (!alreadyChecked) {
        input.checked = true;
      }
    } catch (error) {
      try {
        input.checked = true;
      } catch (innerError) {
        return false;
      }
    }

    if (!input.checked) {
      try {
        input.checked = true;
      } catch (error) {
        return false;
      }
    }

    if (!alreadyChecked || !input.checked) {
      try {
        var inputEvent;
        var changeEvent;
        if (typeof Event === 'function') {
          inputEvent = new Event('input', { bubbles: true });
          changeEvent = new Event('change', { bubbles: true });
        } else {
          inputEvent = document.createEvent('Event');
          inputEvent.initEvent('input', true, true);
          changeEvent = document.createEvent('Event');
          changeEvent.initEvent('change', true, true);
        }
        input.dispatchEvent(inputEvent);
        input.dispatchEvent(changeEvent);
      } catch (error) {
        /* Recharge may still react to the checked state */
      }
    }

    return !!input.checked;
  }

  function whenRechargePlansReady(callback, preferredRoot) {
    var attempts = 0;
    var observer = null;
    var timer = null;
    var debounceTimer = null;
    var settled = false;

    function finish(plans) {
      if (settled) return;
      settled = true;
      if (timer) {
        window.clearTimeout(timer);
        timer = null;
      }
      if (debounceTimer) {
        window.clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      callback(plans || []);
    }

    function poll() {
      if (settled) return;
      var plans = discoverRechargePlans(preferredRoot);
      if (plans.length) {
        finish(plans);
        return;
      }
      attempts += 1;
      if (attempts >= RECHARGE_WAIT_MAX) {
        finish([]);
        return;
      }
      timer = window.setTimeout(poll, RECHARGE_WAIT_MS);
    }

    var immediate = discoverRechargePlans(preferredRoot);
    if (immediate.length) {
      finish(immediate);
      return;
    }

    if (typeof MutationObserver === 'function') {
      observer = new MutationObserver(function () {
        if (settled) return;
        if (debounceTimer) return;
        debounceTimer = window.setTimeout(function () {
          debounceTimer = null;
          if (settled) return;
          var plans = discoverRechargePlans(preferredRoot);
          if (plans.length) finish(plans);
        }, RECHARGE_MUTATION_DEBOUNCE_MS);
      });
      try {
        /* childList only — avoid attribute thrash; capped poll remains the fallback */
        observer.observe(document.body || document.documentElement, {
          childList: true,
          subtree: true
        });
      } catch (error) {
        observer = null;
      }
    }

    poll();
  }

  function readVariantsData(instance) {
    var jsonNode = instance.root.querySelector('[data-basari-variants-json]');
    return parseJsonScript(jsonNode);
  }

  function readVariantPrice(instance, variantId) {
    var result = {
      id: variantId || null,
      price: null,
      compareAtPrice: null,
      priceFormatted: null,
      compareAtPriceFormatted: null,
      featuredMediaId: null,
      imageSrc: null
    };
    var data = readVariantsData(instance);
    var variants = data && data.variants ? data.variants : [];
    var media = data && data.media ? data.media : [];
    var i;
    var targetId = variantId != null ? String(variantId) : '';
    var featuredMediaId = null;
    for (i = 0; i < variants.length; i += 1) {
      if (String(variants[i].id) === targetId) {
        result.id = variants[i].id;
        result.price = variants[i].price;
        result.compareAtPrice = variants[i].compareAtPrice;
        result.priceFormatted = variants[i].priceFormatted;
        result.compareAtPriceFormatted = variants[i].compareAtPriceFormatted;
        featuredMediaId = variants[i].featuredMediaId;
        result.featuredMediaId = featuredMediaId;
        break;
      }
    }
    if (featuredMediaId != null) {
      for (i = 0; i < media.length; i += 1) {
        if (String(media[i].id) === String(featuredMediaId) && media[i].src) {
          result.imageSrc = media[i].src;
          break;
        }
      }
    }
    return result;
  }

  function getSubscribeModal(instance) {
    if (!instance || !instance.dom) return null;
    var mount = instance.dom.subscribeMount;
    if (!mount) return null;
    return mount.querySelector('[data-basari-cl-subscribe-modal]');
  }

  /**
   * Prefer live PDP root attribute (updated by basari-product-variants.js)
   * over the form hidden input, which can lag until we sync it.
   */
  function getCurrentVariantId(instance, modal) {
    var fromRoot = instance && instance.root && instance.root.getAttribute('data-variant-id');
    if (fromRoot) return String(fromRoot);
    if (instance && instance.dom && instance.dom.variantInput && instance.dom.variantInput.value) {
      return String(instance.dom.variantInput.value);
    }
    if (modal && modal.getAttribute('data-variant-id')) {
      return String(modal.getAttribute('data-variant-id'));
    }
    return '';
  }

  function syncVariantFormInput(instance, variantId) {
    if (!instance || !instance.dom || !instance.dom.variantInput || variantId == null || variantId === '') {
      return;
    }
    instance.dom.variantInput.value = String(variantId);
  }

  function getFormNamedControls(form, name) {
    var controls = [];
    var i;
    if (!form || !name || !form.elements) return controls;
    for (i = 0; i < form.elements.length; i += 1) {
      if (form.elements[i] && form.elements[i].name === name) {
        controls.push(form.elements[i]);
      }
    }
    return controls;
  }

  function setFormNamedValue(form, name, value) {
    var controls = getFormNamedControls(form, name);
    var i;
    if (!controls.length) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
      controls.push(input);
    }
    for (i = 0; i < controls.length; i += 1) {
      controls[i].value = value;
    }
    return controls[0];
  }

  function getCheckoutReturnTo() {
    return '/checkout';
  }

  function readSellingPlanIdFromInput(input) {
    if (!input) return '';
    var raw = String(input.value || '').trim();
    if (!raw) {
      raw = String(
        input.getAttribute('data-selling-plan-id') ||
          input.getAttribute('data-selling_plan') ||
          ''
      ).trim();
    }
    if (!raw || raw === '0' || raw === 'onetime' || raw === 'one-time' || raw === 'otp') {
      return '';
    }
    return raw;
  }

  function normalizeSellingPlanId(id) {
    var raw = String(id || '').trim();
    var match = raw.match(/(\d+)\s*$/);
    return match ? match[1] : raw;
  }

  /**
   * Checkout price for a variant + selling plan, from Shopify allocations.
   * Recharge discounts appear here as selling_plan price adjustments.
   * Not a Recharge API call; interval count is never a multiplier.
   */
  function findSellingPlanAllocation(instance, variantId, sellingPlanId) {
    var want = normalizeSellingPlanId(sellingPlanId);
    if (!want || variantId == null || variantId === '') return null;
    var data = readVariantsData(instance);
    var variants = data && data.variants ? data.variants : [];
    var i;
    var j;
    var allocations;
    for (i = 0; i < variants.length; i += 1) {
      if (String(variants[i].id) !== String(variantId)) continue;
      allocations = variants[i].sellingPlanAllocations || [];
      for (j = 0; j < allocations.length; j += 1) {
        if (normalizeSellingPlanId(allocations[j].sellingPlanId) === want) {
          return allocations[j];
        }
      }
      return null;
    }
    return null;
  }

  function resolveSubscribeDisplayPrice(instance, modal) {
    var currency = modal.getAttribute('data-currency') || '';
    var unitPrice = Number(modal.getAttribute('data-unit-price') || 0);
    var unitFormatted =
      modal.getAttribute('data-unit-price-formatted') || formatMoneyCents(unitPrice, currency);
    var compareAt = Number(modal.getAttribute('data-compare-at-price') || 0);
    var compareFormatted =
      modal.getAttribute('data-compare-at-price-formatted') ||
      (compareAt > 0 ? formatMoneyCents(compareAt, currency) : '');
    var result = {
      price: unitPrice,
      priceFormatted: unitFormatted,
      compareAt: compareAt,
      compareFormatted: compareFormatted
    };
    var selectedPlan = getSelectedSubscribePlan(instance, modal);
    var sellingPlanId = readSellingPlanIdFromInput(selectedPlan && selectedPlan.input);
    var allocation = findSellingPlanAllocation(
      instance,
      getCurrentVariantId(instance, modal),
      sellingPlanId
    );
    if (!allocation || allocation.price == null) return result;
    result.price = Number(allocation.price);
    result.priceFormatted =
      allocation.priceFormatted || formatMoneyCents(result.price, currency);
    if (allocation.compareAtPrice != null && Number(allocation.compareAtPrice) > result.price) {
      result.compareAt = Number(allocation.compareAtPrice);
      result.compareFormatted =
        allocation.compareAtPriceFormatted || formatMoneyCents(result.compareAt, currency);
    }
    return result;
  }

  function getSelectedSubscribePlan(instance, modal) {
    var planState = getModalPlanState(modal);
    var discoveryRoot =
      (modal && modal._basariDiscoveryRoot) || getSubscribeDiscoveryRoot(instance, modal);
    var plans =
      (modal && modal._basariRechargePlans) || discoverRechargePlans(discoveryRoot);
    var plan = null;
    if (planState && planState.normalizedLabel) {
      plan = findPlanByNormalized(plans, planState.normalizedLabel);
    }
    if (!plan) {
      plan = getCheckedRechargePlan(plans);
    }
    return plan || null;
  }

  function getCartLineQuantity(form) {
    var qtyInput = getFormNamedControls(form, 'quantity')[0];
    var qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    if (!qty || qty < 1 || isNaN(qty)) return 1;
    return qty;
  }

  function submitCartFormPreservingListeners(form) {
    if (!form) return;
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
      return;
    }
    var submitEvent;
    try {
      submitEvent = new Event('submit', { bubbles: true, cancelable: true });
    } catch (error) {
      submitEvent = document.createEvent('Event');
      submitEvent.initEvent('submit', true, true);
    }
    if (form.dispatchEvent(submitEvent) !== false && !submitEvent.defaultPrevented) {
      HTMLFormElement.prototype.submit.call(form);
    }
  }

  /**
   * Sync the existing cart-add form with the modal's selected Recharge plan
   * and current variant, then requestSubmit() so native submit listeners run.
   * return_to=/checkout asks Shopify's HTML /cart/add to skip the cart page.
   */
  function prepareAndSubmitSubscribeCart(instance, modal, proceedBtn) {
    var form = instance && instance.dom ? instance.dom.form : null;
    var plan = getSelectedSubscribePlan(instance, modal);
    var sellingPlanId = readSellingPlanIdFromInput(plan && plan.input);
    var variantId = getCurrentVariantId(instance, modal);
    var quantity;

    if (!prepareContactLensPurchase(instance)) {
      closeSubscribeModal(instance, modal);
      return false;
    }

    if (!form || !plan || !sellingPlanId || !variantId) return false;

    activateRechargePlan(plan);
    sellingPlanId = readSellingPlanIdFromInput(plan.input) || sellingPlanId;
    if (!sellingPlanId) return false;

    syncVariantFormInput(instance, variantId);
    setFormNamedValue(form, 'id', String(variantId));
    var sellingPlanControl = setFormNamedValue(form, 'selling_plan', sellingPlanId);
    if (sellingPlanControl) sellingPlanControl.disabled = false;
    var planControls = getFormNamedControls(form, 'selling_plan');
    var p;
    for (p = 0; p < planControls.length; p += 1) {
      planControls[p].disabled = false;
      planControls[p].value = sellingPlanId;
    }
    quantity = getCartLineQuantity(form);
    setFormNamedValue(form, 'quantity', String(quantity));
    setFormNamedValue(form, 'return_to', getCheckoutReturnTo());

    if (proceedBtn) {
      proceedBtn.disabled = true;
      proceedBtn.setAttribute('aria-busy', 'true');
    }

    form.addEventListener(
      'submit',
      function (event) {
        if (!event.defaultPrevented || !proceedBtn) return;
        proceedBtn.disabled = false;
        proceedBtn.removeAttribute('aria-busy');
      },
      { once: true }
    );

    try {
      submitCartFormPreservingListeners(form);
    } catch (error) {
      if (proceedBtn) {
        proceedBtn.disabled = false;
        proceedBtn.removeAttribute('aria-busy');
      }
      return false;
    }

    return true;
  }

  function getSelectedPackLabel(instance) {
    if (!instance || !instance.dom || !instance.dom.packCards) return '';
    var i;
    for (i = 0; i < instance.dom.packCards.length; i += 1) {
      var card = instance.dom.packCards[i];
      if (card.getAttribute('data-selected') === 'true' || card.getAttribute('aria-checked') === 'true') {
        return card.getAttribute('data-pack-label') || '';
      }
    }
    return '';
  }

  function getSelectedPackValue(instance) {
    if (instance && instance.dom && instance.dom.packInput) {
      return instance.dom.packInput.value || '';
    }
    if (!instance || !instance.dom || !instance.dom.packCards) return '';
    var i;
    for (i = 0; i < instance.dom.packCards.length; i += 1) {
      var card = instance.dom.packCards[i];
      if (card.getAttribute('data-selected') === 'true' || card.getAttribute('aria-checked') === 'true') {
        return card.getAttribute('data-pack-value') || '';
      }
    }
    return '';
  }

  function setText(node, value) {
    if (!node) return;
    node.textContent = value == null ? '' : String(value);
  }

  function setHidden(node, hidden) {
    if (!node) return;
    if (hidden) {
      node.setAttribute('hidden', '');
    } else {
      node.removeAttribute('hidden');
    }
  }

  function replaceToken(template, token, value) {
    return String(template || '').split(token).join(String(value));
  }

  function getModalFrequency(modal) {
    var freqBtn = modal.querySelector(
      '[data-basari-cl-subscribe-freq-option][aria-checked="true"]'
    );
    return freqBtn ? freqBtn.getAttribute('data-frequency') : 'weekly';
  }

  function getModalPlanState(modal) {
    var select = modal.querySelector('[data-basari-cl-subscribe-plan-select]');
    var normalized = select && select.value ? canonicalizePlanLabel(select.value) : '';
    var display =
      (select && select.options && select.selectedIndex >= 0
        ? select.options[select.selectedIndex].text
        : '') || formatPlanDisplayLabel(normalized);
    var duration = getPlanCountFromLabel(normalized || modal.getAttribute('data-plan-label') || '');
    var frequency = getPlanTypeFromLabel(normalized) || getModalFrequency(modal) || 'weekly';
    return {
      normalizedLabel: normalized,
      displayLabel: display,
      duration: duration,
      frequency: frequency
    };
  }

  function syncSubscribeSummary(instance, modal) {
    if (!modal) return;
    var strings = parseJsonScript(modal.querySelector('[data-basari-cl-subscribe-strings]'));
    var planState = getModalPlanState(modal);
    var frequency = planState.frequency;
    /* Interval count from plan label (e.g. 5 weeks → 5). Not cart line quantity. */
    var intervalCount = planState.duration;

    var currency = modal.getAttribute('data-currency') || '';
    var priced = resolveSubscribeDisplayPrice(instance, modal);
    var displayPrice = priced.price;
    var displayFormatted = priced.priceFormatted;
    var compareAt = priced.compareAt;
    var hasCompare = compareAt > displayPrice && displayPrice >= 0;

    setText(
      modal.querySelector('[data-basari-cl-subscribe-duration-value]'),
      planState.displayLabel || '—'
    );
    setText(
      modal.querySelector('[data-basari-cl-subscribe-summary-product]'),
      strings.productLabel && strings.productLabel[frequency]
    );
    /* Summary "Interval" shows the plan label, not a bare multiplier. */
    setText(
      modal.querySelector('[data-basari-cl-subscribe-summary-qty]'),
      planState.displayLabel || '—'
    );

    setText(modal.querySelector('[data-basari-cl-subscribe-price]'), displayFormatted);
    /*
     * Subscription charge is the selling-plan allocation price when present,
     * otherwise the selected variant price (data-unit-price). Interval count
     * is recurrence frequency only — never a price multiplier.
     */
    setText(modal.querySelector('[data-basari-cl-subscribe-summary-price]'), displayFormatted);

    var cadenceNode = modal.querySelector('[data-basari-cl-subscribe-pricing-note]');
    if (cadenceNode) {
      if (planState.displayLabel && planState.displayLabel !== '—') {
        setText(
          cadenceNode,
          replaceToken(
            replaceToken(strings.priceCadence || '__PRICE__ every __INTERVAL__', '__PRICE__', displayFormatted),
            '__INTERVAL__',
            planState.displayLabel
          )
        );
      } else {
        setText(cadenceNode, strings.pricingNote || '');
      }
    }

    var compareNode = modal.querySelector('[data-basari-cl-subscribe-compare]');
    var badgeNode = modal.querySelector('[data-basari-cl-subscribe-badge]');
    var saveNode = modal.querySelector('[data-basari-cl-subscribe-save]');
    var summaryCompare = modal.querySelector('[data-basari-cl-subscribe-summary-compare]');
    var banner = modal.querySelector('[data-basari-cl-subscribe-banner]');

    setHidden(banner, !FREQ_SHOW_BANNER[frequency]);

    if (hasCompare) {
      var compareFormatted =
        priced.compareFormatted || formatMoneyCents(compareAt, currency);
      var savings = compareAt - displayPrice;
      var percent = Math.round((savings * 100) / compareAt);
      setText(compareNode, compareFormatted);
      setText(summaryCompare, compareFormatted);
      setText(
        badgeNode,
        replaceToken(strings.percentOff, '__P__', percent)
      );
      setText(
        saveNode,
        replaceToken(strings.saveAmount, '__A__', formatMoneyCents(savings, currency))
      );
      setHidden(compareNode, false);
      setHidden(summaryCompare, false);
      setHidden(badgeNode, percent <= 0);
      setHidden(saveNode, savings <= 0);
    } else {
      setHidden(compareNode, true);
      setHidden(summaryCompare, true);
      setHidden(badgeNode, true);
      setHidden(saveNode, true);
    }

    modal.setAttribute('data-frequency', frequency);
    modal.setAttribute('data-duration', String(intervalCount));
    modal.setAttribute('data-interval-count', String(intervalCount));
    /*
     * Legacy alias: original frontend estimate used quantity === duration.
     * This is NOT cart quantity. Keep for existing proceed payload shape.
     */
    modal.setAttribute('data-quantity', String(intervalCount));
    if (planState.normalizedLabel) {
      modal.setAttribute('data-plan-label', planState.normalizedLabel);
      modal.setAttribute('data-plan-display', planState.displayLabel);
    }
  }

  function syncSubscribeImage(modal, imageSrc, productTitle) {
    if (!modal) return;
    var media = modal.querySelector('[data-basari-cl-subscribe-media]');
    if (!media) return;
    var img = media.querySelector('[data-basari-cl-subscribe-image], .basari-cl-subscribe-modal__image');
    var placeholder = media.querySelector(
      '[data-basari-cl-subscribe-image-placeholder], .basari-cl-subscribe-modal__image-placeholder'
    );
    var alt = productTitle || modal.getAttribute('data-product-title') || '';
    var nextSrc = imageSrc || modal.getAttribute('data-default-image') || '';

    if (nextSrc) {
      if (!img) {
        if (placeholder) placeholder.parentNode.removeChild(placeholder);
        img = document.createElement('img');
        img.className = 'basari-cl-subscribe-modal__image';
        img.setAttribute('data-basari-cl-subscribe-image', '');
        img.loading = 'lazy';
        media.appendChild(img);
      }
      if (img.getAttribute('src') !== nextSrc) {
        img.setAttribute('src', nextSrc);
      }
      img.setAttribute('alt', alt);
      setHidden(img, false);
      if (placeholder) setHidden(placeholder, true);
      return;
    }

    if (img) {
      img.setAttribute('alt', alt);
    }
  }

  function syncSubscribeProductState(instance, modal) {
    if (!modal || !instance) return;
    var variantId = getCurrentVariantId(instance, modal);
    syncVariantFormInput(instance, variantId);

    var priced = readVariantPrice(instance, variantId);
    var currency = modal.getAttribute('data-currency') || '';
    var productTitle =
      modal.getAttribute('data-product-title') ||
      (instance.config && instance.config.productTitle) ||
      '';

    if (priced.price != null) {
      modal.setAttribute('data-unit-price', String(priced.price));
      modal.setAttribute(
        'data-unit-price-formatted',
        priced.priceFormatted || formatMoneyCents(priced.price, currency)
      );
    }
    if (priced.compareAtPrice != null && priced.compareAtPrice > priced.price) {
      modal.setAttribute('data-compare-at-price', String(priced.compareAtPrice));
      modal.setAttribute(
        'data-compare-at-price-formatted',
        priced.compareAtPriceFormatted || formatMoneyCents(priced.compareAtPrice, currency)
      );
    } else {
      modal.removeAttribute('data-compare-at-price');
      modal.removeAttribute('data-compare-at-price-formatted');
    }
    if (priced.id != null) {
      modal.setAttribute('data-variant-id', String(priced.id));
    }

    setText(
      modal.querySelector('[data-basari-cl-subscribe-product-title]'),
      productTitle
    );
    syncSubscribeImage(modal, priced.imageSrc, productTitle);

    var packLabel = getSelectedPackLabel(instance);
    var packNode = modal.querySelector('[data-basari-cl-subscribe-pack-label]');
    if (packNode) {
      if (packLabel) {
        setText(packNode, packLabel);
        setHidden(packNode, false);
      } else {
        setText(packNode, '');
        setHidden(packNode, true);
      }
    }
    modal.setAttribute('data-pack-value', getSelectedPackValue(instance));
    modal.setAttribute('data-pack-label', packLabel);
  }

  function updateFrequencyTabVisibility(modal, groups) {
    var opts = modal.querySelectorAll('[data-basari-cl-subscribe-freq-option]');
    var i;
    for (i = 0; i < opts.length; i += 1) {
      var freq = opts[i].getAttribute('data-frequency');
      var hasPlans = !!(groups[freq] && groups[freq].length);
      setHidden(opts[i], !hasPlans);
      if (!hasPlans) {
        opts[i].setAttribute('aria-checked', 'false');
        opts[i].setAttribute('tabindex', '-1');
        opts[i].classList.remove('basari-cl-subscribe-modal__freq-btn--active');
      }
    }
  }

  function setFrequencyTabs(modal, frequency, options) {
    var opts = modal.querySelectorAll('[data-basari-cl-subscribe-freq-option]');
    var i;
    var activeBtn = null;
    for (i = 0; i < opts.length; i += 1) {
      if (opts[i].hasAttribute('hidden')) continue;
      var active = opts[i].getAttribute('data-frequency') === frequency;
      opts[i].setAttribute('aria-checked', active ? 'true' : 'false');
      opts[i].setAttribute('tabindex', active ? '0' : '-1');
      opts[i].classList.toggle('basari-cl-subscribe-modal__freq-btn--active', active);
      if (active) activeBtn = opts[i];
    }
    if (options && options.focus && activeBtn && typeof activeBtn.focus === 'function') {
      activeBtn.focus();
    }
    return activeBtn;
  }

  function populatePlanSelect(modal, groupPlans, selectedNormalized, strings) {
    var select = modal.querySelector('[data-basari-cl-subscribe-plan-select]');
    if (!select) return null;

    var previous = selectedNormalized || select.value || '';
    select.innerHTML = '';

    if (!groupPlans || !groupPlans.length) {
      var empty = document.createElement('option');
      empty.value = '';
      empty.disabled = true;
      empty.selected = true;
      empty.textContent =
        (strings && strings.planEmpty) || 'No plans available';
      select.appendChild(empty);
      select.disabled = true;
      return null;
    }

    select.disabled = false;
    var matched = null;
    var i;
    for (i = 0; i < groupPlans.length; i += 1) {
      var plan = groupPlans[i];
      var option = document.createElement('option');
      option.value = plan.normalizedLabel;
      option.textContent = plan.displayLabel;
      if (canonicalizePlanLabel(previous) === plan.normalizedLabel) {
        option.selected = true;
        matched = plan;
      }
      select.appendChild(option);
    }

    if (!matched) {
      select.selectedIndex = 0;
      matched = groupPlans[0];
    }

    return matched;
  }

  function syncSubscribeUiFromRecharge(instance, modal, options) {
    if (!modal) return;
    var opts = options || {};
    var strings = parseJsonScript(modal.querySelector('[data-basari-cl-subscribe-strings]'));
    var discoveryRoot =
      opts.discoveryRoot ||
      modal._basariDiscoveryRoot ||
      getSubscribeDiscoveryRoot(instance, modal);
    modal._basariDiscoveryRoot = discoveryRoot;
    var plans = opts.plans || discoverRechargePlans(discoveryRoot);
    var groups = groupRechargePlans(plans);
    modal._basariRechargePlans = plans;
    modal._basariRechargeGroups = groups;
    attachRechargeInputListeners(instance, modal, plans);

    updateFrequencyTabVisibility(modal, groups);

    var checked = getCheckedRechargePlan(plans);
    var preferredFrequency =
      opts.frequency ||
      (checked && checked.type) ||
      getModalFrequency(modal) ||
      'weekly';

    if (!groups[preferredFrequency] || !groups[preferredFrequency].length) {
      var f;
      preferredFrequency = null;
      for (f = 0; f < FREQ_ORDER.length; f += 1) {
        if (groups[FREQ_ORDER[f]] && groups[FREQ_ORDER[f]].length) {
          preferredFrequency = FREQ_ORDER[f];
          break;
        }
      }
    }

    if (!preferredFrequency) {
      populatePlanSelect(modal, [], null, strings);
      syncSubscribeSummary(instance, modal);
      return;
    }

    setFrequencyTabs(modal, preferredFrequency, { focus: !!opts.focus });

    var selectedNormalized =
      opts.normalizedLabel ||
      (checked && checked.type === preferredFrequency ? checked.normalizedLabel : '') ||
      modal.getAttribute('data-plan-label') ||
      '';

    var selectedPlan = populatePlanSelect(
      modal,
      groups[preferredFrequency],
      selectedNormalized,
      strings
    );

    if (selectedPlan && opts.activate !== false) {
      if (!checked || checked.normalizedLabel !== selectedPlan.normalizedLabel) {
        activateRechargePlan(selectedPlan);
      }
    }

    syncSubscribeSummary(instance, modal);
  }

  function applyFrequencySelection(instance, modal, frequency, options) {
    if (!modal || !frequency) return;
    var discoveryRoot =
      modal._basariDiscoveryRoot || getSubscribeDiscoveryRoot(instance, modal);
    var groups =
      modal._basariRechargeGroups ||
      groupRechargePlans(discoverRechargePlans(discoveryRoot));
    var plans = groups[frequency] || [];
    if (!plans.length) {
      syncSubscribeUiFromRecharge(instance, modal, {
        frequency: frequency,
        activate: false,
        discoveryRoot: discoveryRoot
      });
      return;
    }

    setFrequencyTabs(modal, frequency, options);
    var strings = parseJsonScript(modal.querySelector('[data-basari-cl-subscribe-strings]'));
    var checked = getCheckedRechargePlan(
      modal._basariRechargePlans || discoverRechargePlans(discoveryRoot)
    );
    var preferred =
      checked && checked.type === frequency ? checked.normalizedLabel : plans[0].normalizedLabel;
    var selected = populatePlanSelect(modal, plans, preferred, strings);
    if (selected) activateRechargePlan(selected);
    syncSubscribeSummary(instance, modal);
  }

  function applyPlanSelectChange(instance, modal) {
    var select = modal.querySelector('[data-basari-cl-subscribe-plan-select]');
    if (!select || !select.value) return;
    var discoveryRoot =
      modal._basariDiscoveryRoot || getSubscribeDiscoveryRoot(instance, modal);
    var plans = modal._basariRechargePlans || discoverRechargePlans(discoveryRoot);
    var plan = findPlanByNormalized(plans, select.value);
    if (!plan) {
      plans = discoverRechargePlans(discoveryRoot);
      modal._basariRechargePlans = plans;
      modal._basariRechargeGroups = groupRechargePlans(plans);
      plan = findPlanByNormalized(plans, select.value);
    }
    if (plan) {
      activateRechargePlan(plan);
      setFrequencyTabs(modal, plan.type);
    }
    syncSubscribeSummary(instance, modal);
  }

  function getVisibleFrequencies(modal) {
    var list = [];
    var i;
    for (i = 0; i < FREQ_ORDER.length; i += 1) {
      var btn = modal.querySelector(
        '[data-basari-cl-subscribe-freq-option][data-frequency="' + FREQ_ORDER[i] + '"]'
      );
      if (btn && !btn.hasAttribute('hidden')) list.push(FREQ_ORDER[i]);
    }
    return list.length ? list : FREQ_ORDER.slice();
  }

  function moveSubscribeFrequency(instance, modal, currentFrequency, delta) {
    var order = getVisibleFrequencies(modal);
    var index = order.indexOf(currentFrequency);
    if (index < 0) index = 0;
    var nextIndex = (index + delta + order.length) % order.length;
    applyFrequencySelection(instance, modal, order[nextIndex], { focus: true });
  }

  function bindRechargePlanWatcher(instance, modal) {
    if (modal._basariRcWatcherBound) return;
    modal._basariRcWatcherBound = true;

    modal._basariRcChangeHandler = function (event) {
      var target = event && event.target;
      if (!target || target.name !== 'rc_plan') return;
      if (modal.hasAttribute('hidden')) return;
      if (modal._basariSyncingFromUi) return;
      syncSubscribeUiFromRecharge(instance, modal, {
        activate: false,
        plans: discoverRechargePlans(
          modal._basariDiscoveryRoot || getSubscribeDiscoveryRoot(instance, modal)
        )
      });
    };

    document.addEventListener('change', modal._basariRcChangeHandler, true);
  }

  function attachRechargeInputListeners(instance, modal, plans) {
    if (!plans || !plans.length) return;
    if (!modal._basariRcBoundInputs) {
      modal._basariRcBoundInputs = typeof WeakSet === 'function' ? new WeakSet() : [];
    }
    var i;
    for (i = 0; i < plans.length; i += 1) {
      var input = plans[i].input;
      if (!input) continue;

      if (modal._basariRcBoundInputs.add) {
        if (modal._basariRcBoundInputs.has(input)) continue;
        modal._basariRcBoundInputs.add(input);
      } else if (modal._basariRcBoundInputs.indexOf(input) !== -1) {
        continue;
      } else {
        modal._basariRcBoundInputs.push(input);
      }

      input.addEventListener('change', function () {
        if (modal.hasAttribute('hidden')) return;
        if (modal._basariSyncingFromUi) return;
        syncSubscribeUiFromRecharge(instance, modal, {
          activate: false,
          plans: discoverRechargePlans(
            modal._basariDiscoveryRoot || getSubscribeDiscoveryRoot(instance, modal)
          )
        });
      });
    }
  }

  function closeSubscribeModal(instance, modal) {
    if (!modal || modal.hasAttribute('hidden')) return;
    modal.setAttribute('hidden', '');
    document.documentElement.classList.remove(BODY_OPEN_CLASS);
    document.body.classList.remove(BODY_OPEN_CLASS);

    if (instance.dom.subscribeOpen) {
      instance.dom.subscribeOpen.setAttribute('aria-expanded', 'false');
    }

    if (modal._basariKeyHandler) {
      document.removeEventListener('keydown', modal._basariKeyHandler, true);
      modal._basariKeyHandler = null;
    }

    var trigger = modal._basariReturnFocus || instance.dom.subscribeOpen;
    modal._basariReturnFocus = null;
    if (trigger && typeof trigger.focus === 'function') {
      window.setTimeout(function () {
        trigger.focus();
      }, 0);
    }

    dispatch(instance.root, 'basari:cl:subscribe-close', {
      sectionId: instance.config.sectionId
    });
  }

  function openSubscribeModal(instance) {
    var modal = getSubscribeModal(instance);
    if (!modal) return;

    syncSubscribeProductState(instance, modal);

    modal._basariReturnFocus = document.activeElement;
    modal.removeAttribute('hidden');
    document.documentElement.classList.add(BODY_OPEN_CLASS);
    document.body.classList.add(BODY_OPEN_CLASS);

    if (instance.dom.subscribeOpen) {
      instance.dom.subscribeOpen.setAttribute('aria-expanded', 'true');
    }

    var dialog = modal.querySelector('[data-basari-cl-subscribe-dialog]');
    window.setTimeout(function () {
      if (dialog && typeof dialog.focus === 'function') {
        dialog.focus();
      }
    }, 0);

    if (!modal._basariKeyHandler) {
      modal._basariKeyHandler = function (event) {
        if (modal.hasAttribute('hidden')) return;
        if (event.key === 'Escape' || event.keyCode === 27) {
          event.preventDefault();
          closeSubscribeModal(instance, modal);
          return;
        }
        if (event.key !== 'Tab') return;
        var focusables = getFocusable(dialog || modal);
        if (!focusables.length) return;
        var first = focusables[0];
        var last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', modal._basariKeyHandler, true);
    }

    if (!modal.getAttribute('data-basari-cl-subscribe-bound')) {
      modal.setAttribute('data-basari-cl-subscribe-bound', 'true');

      var backdrop = modal.querySelector('[data-basari-cl-subscribe-backdrop]');
      var backBtn = modal.querySelector('[data-basari-cl-subscribe-back]');
      var closeBtn = modal.querySelector('[data-basari-cl-subscribe-close]');
      var dismissBtn = modal.querySelector('[data-basari-cl-subscribe-dismiss]');
      var proceedBtn = modal.querySelector('[data-basari-subscribe-proceed]');
      var planSelect = modal.querySelector('[data-basari-cl-subscribe-plan-select]');
      var freqOptions = modal.querySelectorAll('[data-basari-cl-subscribe-freq-option]');
      var i;

      function onClose() {
        closeSubscribeModal(instance, modal);
      }

      if (backdrop) backdrop.addEventListener('click', onClose);
      if (backBtn) backBtn.addEventListener('click', onClose);
      if (closeBtn) closeBtn.addEventListener('click', onClose);
      if (dismissBtn) dismissBtn.addEventListener('click', onClose);

      for (i = 0; i < freqOptions.length; i += 1) {
        freqOptions[i].addEventListener('click', function (event) {
          var next = event.currentTarget.getAttribute('data-frequency');
          modal._basariSyncingFromUi = true;
          applyFrequencySelection(instance, modal, next);
          modal._basariSyncingFromUi = false;
        });
        freqOptions[i].addEventListener('keydown', function (event) {
          var current = event.currentTarget.getAttribute('data-frequency');
          var key = event.key;
          var visible = getVisibleFrequencies(modal);
          if (key === 'ArrowRight' || key === 'ArrowDown') {
            event.preventDefault();
            moveSubscribeFrequency(instance, modal, current, 1);
            return;
          }
          if (key === 'ArrowLeft' || key === 'ArrowUp') {
            event.preventDefault();
            moveSubscribeFrequency(instance, modal, current, -1);
            return;
          }
          if (key === 'Home') {
            event.preventDefault();
            applyFrequencySelection(instance, modal, visible[0], { focus: true });
            return;
          }
          if (key === 'End') {
            event.preventDefault();
            applyFrequencySelection(instance, modal, visible[visible.length - 1], {
              focus: true
            });
          }
        });
      }

      if (planSelect) {
        planSelect.addEventListener('change', function () {
          modal._basariSyncingFromUi = true;
          applyPlanSelectChange(instance, modal);
          modal._basariSyncingFromUi = false;
        });
      }

      if (proceedBtn) {
        proceedBtn.addEventListener('click', function () {
          if (proceedBtn.getAttribute('aria-busy') === 'true' || proceedBtn.disabled) {
            return;
          }
          syncSubscribeProductState(instance, modal);
          syncSubscribeSummary(instance, modal);
          var planState = getModalPlanState(modal);
          var selectedPlan = getSelectedSubscribePlan(instance, modal);
          var sellingPlanId = readSellingPlanIdFromInput(selectedPlan && selectedPlan.input);
          /*
           * quantity is a legacy alias of intervalCount for this event only.
           * No theme code listens to basari:cl:subscribe-proceed.
           * Cart line quantity is read separately from the form in
           * prepareAndSubmitSubscribeCart (getCartLineQuantity).
           */
          var intervalCount = Number(modal.getAttribute('data-interval-count') || modal.getAttribute('data-duration') || 0);
          var payload = {
            sectionId: instance.config.sectionId,
            productId: modal.getAttribute('data-product-id'),
            variantId: modal.getAttribute('data-variant-id'),
            packValue: getSelectedPackValue(instance),
            packLabel: getSelectedPackLabel(instance),
            frequency: modal.getAttribute('data-frequency'),
            duration: intervalCount,
            intervalCount: intervalCount,
            planLabel: planState.normalizedLabel || modal.getAttribute('data-plan-label') || '',
            quantity: intervalCount,
            unitPriceCents: Number(modal.getAttribute('data-unit-price') || 0),
            compareAtPriceCents: Number(modal.getAttribute('data-compare-at-price') || 0) || null,
            /* Unused legacy field. Not a cart total and not interval × price. */
            totalCents: null,
            estimateOnly: true,
            sellingPlanId: sellingPlanId || null,
            currency: modal.getAttribute('data-currency')
          };
          if (!sellingPlanId) return;
          dispatch(instance.root, 'basari:cl:subscribe-proceed', payload);
          prepareAndSubmitSubscribeCart(instance, modal, proceedBtn);
        });
      }

      bindRechargePlanWatcher(instance, modal);
    }

    modal._basariDiscoveryRoot = getSubscribeDiscoveryRoot(instance, modal);
    whenRechargePlansReady(function (plans) {
      if (modal.hasAttribute('hidden')) return;
      var checked = getCheckedRechargePlan(plans);
      syncSubscribeUiFromRecharge(instance, modal, {
        plans: plans,
        discoveryRoot: modal._basariDiscoveryRoot,
        /* Reflect an existing Recharge selection; otherwise select a real default plan. */
        activate: !checked
      });
    }, modal._basariDiscoveryRoot);

    dispatch(instance.root, 'basari:cl:subscribe-opened', {
      sectionId: instance.config.sectionId
    });
  }

  extensions.openSubscribeModal = openSubscribeModal;

  var SPH_MODAL_BODY_CLASS = 'basari-cl-sph-modal-open';

  function isSphericalLens(instance) {
    if (!instance || !instance.config) return true;
    if (instance.config.isSpherical === false) return false;
    if (instance.config.isSpherical === true) return true;
    var type = String(instance.config.contactLensType || '')
      .toLowerCase()
      .trim();
    return !type || type === 'spherical';
  }

  function isToricLens(instance) {
    if (!instance || !instance.config) return false;
    if (instance.config.isToric === true) return true;
    var type = String(instance.config.contactLensType || '')
      .toLowerCase()
      .trim();
    return type === 'toric';
  }

  function isMultifocalLens(instance) {
    if (!instance || !instance.config) return false;
    if (instance.config.isMultifocal === true) return true;
    var type = String(instance.config.contactLensType || '')
      .toLowerCase()
      .trim();
    return type === 'multifocal';
  }

  function isSpecialLens(instance) {
    if (!instance || !instance.config) return false;
    if (instance.config.isSpecial === true) return true;
    var type = String(instance.config.contactLensType || '')
      .toLowerCase()
      .trim();
    return type === 'special';
  }

  function usesSphModal(instance) {
    return isSphericalLens(instance) || isToricLens(instance) || isMultifocalLens(instance);
  }

  function usesBoxQuantity(instance) {
    return isSphericalLens(instance) || isToricLens(instance) || isMultifocalLens(instance);
  }

  function getSphericalConfig(instance) {
    var rx =
      instance && instance.config && instance.config.prescription
        ? instance.config.prescription.spherical
        : null;
    rx = rx || {};
    return {
      negativeMin: rx.sphNegativeMin != null ? Number(rx.sphNegativeMin) : -0.25,
      negativeMax: rx.sphNegativeMax != null ? Number(rx.sphNegativeMax) : -14,
      positiveMin: rx.sphPositiveMin != null ? Number(rx.sphPositiveMin) : 0,
      positiveMax: rx.sphPositiveMax != null ? Number(rx.sphPositiveMax) : 10,
      step: rx.sphStep != null ? Number(rx.sphStep) : 0.25,
      maxBoxes: rx.maxBoxesPerEye != null ? parseInt(rx.maxBoxesPerEye, 10) : 10
    };
  }

  function getToricConfig(instance) {
    var rx =
      instance && instance.config && instance.config.prescription
        ? instance.config.prescription.toric
        : null;
    rx = rx || {};
    var cyl = rx.cylValues;
    var list = [];
    var i;
    if (typeof cyl === 'string') {
      cyl = cyl.split(',');
    }
    if (cyl && cyl.length) {
      for (i = 0; i < cyl.length; i += 1) {
        list.push(String(cyl[i]).trim());
      }
    }
    if (!list.length) {
      list = ['0', '-0.75', '-1.25', '-1.75', '-2.25', '-2.75'];
    }
    return {
      cylValues: list,
      axisMin: rx.axisMin != null ? Number(rx.axisMin) : 0,
      axisMax: rx.axisMax != null ? Number(rx.axisMax) : 180,
      axisStep: rx.axisStep != null ? Number(rx.axisStep) : 10,
      maxBoxes: rx.maxBoxesPerEye != null ? parseInt(rx.maxBoxesPerEye, 10) : 10
    };
  }

  function getMultifocalConfig(instance) {
    var rx =
      instance && instance.config && instance.config.prescription
        ? instance.config.prescription.multifocal
        : null;
    rx = rx || {};
    var raw = rx.addBands;
    var list = [];
    var i;
    if (raw && raw.length) {
      for (i = 0; i < raw.length; i += 1) {
        var band = raw[i] || {};
        var value = String(band.value || '').trim();
        if (!value) continue;
        list.push({
          value: value,
          label: String(band.label || value).trim() || value,
          range: String(band.range || '').trim()
        });
      }
    }
    return {
      addBands: list,
      maxBoxes: rx.maxBoxesPerEye != null ? parseInt(rx.maxBoxesPerEye, 10) : 10
    };
  }

  function getSpecialConfig(instance) {
    var rx =
      instance && instance.config && instance.config.prescription
        ? instance.config.prescription.special
        : null;
    rx = rx || {};
    return {
      sphereMin: rx.sphereMin != null ? Number(rx.sphereMin) : -25,
      sphereMax: rx.sphereMax != null ? Number(rx.sphereMax) : 20,
      sphereStep: rx.sphereStep != null ? Number(rx.sphereStep) : 0.25,
      cylinderMin: rx.cylinderMin != null ? Number(rx.cylinderMin) : -10,
      cylinderMax: rx.cylinderMax != null ? Number(rx.cylinderMax) : 10,
      cylinderStep: rx.cylinderStep != null ? Number(rx.cylinderStep) : 0.25,
      axisMin: rx.axisMin != null ? Number(rx.axisMin) : 1,
      axisMax: rx.axisMax != null ? Number(rx.axisMax) : 180,
      axisStep: rx.axisStep != null ? Number(rx.axisStep) : 1,
      addMin: rx.addMin != null ? Number(rx.addMin) : 0.75,
      addMax: rx.addMax != null ? Number(rx.addMax) : 6,
      addStep: rx.addStep != null ? Number(rx.addStep) : 0.25
    };
  }

  function getPowerMode(instance) {
    if (instance && instance.dom && instance.dom.power) {
      return instance.dom.power.getAttribute('data-power-mode') || '';
    }
    return '';
  }

  function buildSphHundredths(min, max, step) {
    var start = Math.round(Number(min) * 100);
    var end = Math.round(Number(max) * 100);
    var inc = Math.round(Number(step) * 100) || 25;
    var values = [];
    var n;
    if (!inc) return values;
    if (start <= end) {
      for (n = start; n <= end; n += inc) values.push(n);
    } else {
      for (n = start; n >= end; n -= inc) values.push(n);
    }
    return values;
  }

  function formatSphValue(hundredths) {
    var abs = Math.abs(hundredths);
    var whole = Math.floor(abs / 100);
    var frac = abs % 100;
    var body = String(whole) + '.' + ('0' + frac).slice(-2);
    if (hundredths < 0) return '-' + body;
    if (hundredths > 0) return '+' + body;
    return '0.00';
  }

  function queryEyeControl(instance, selector, eye) {
    if (!instance || !instance.root) return null;
    var nodes = instance.root.querySelectorAll(selector);
    var i;
    for (i = 0; i < nodes.length; i += 1) {
      if (nodes[i].getAttribute('data-eye') === eye) return nodes[i];
    }
    return null;
  }

  function getEyeCheckbox(instance, eye) {
    return queryEyeControl(instance, '[data-basari-cl-power-eye-input]', eye);
  }

  function isEyeChecked(instance, eye) {
    if (isSpecialLens(instance)) return true;
    var input = getEyeCheckbox(instance, eye);
    return !!(input && input.checked);
  }

  function getSphInput(instance, eye) {
    return queryEyeControl(
      instance,
      'input[data-basari-cl-rx-field-input][data-field="sph"]',
      eye
    );
  }

  function getSphTrigger(instance, eye) {
    return queryEyeControl(instance, '[data-basari-cl-sph-trigger]', eye);
  }

  function getBoxesInput(instance, eye) {
    return queryEyeControl(
      instance,
      '[data-basari-cl-rx-field-input][data-field="boxes"], [data-basari-cl-power-boxes]',
      eye
    );
  }

  function getSphError(instance, eye) {
    return queryEyeControl(instance, '[data-basari-cl-sph-error]', eye);
  }

  function getSphLiveValue(instance, eye) {
    var input = getSphInput(instance, eye);
    return input ? String(input.value || '').trim() : '';
  }

  function getPlaceholderLabel(instance, eye) {
    var trigger = getSphTrigger(instance, eye) || (instance.dom.sphTriggers && instance.dom.sphTriggers[0]);
    if (trigger && trigger.getAttribute('data-placeholder')) {
      return trigger.getAttribute('data-placeholder');
    }
    return 'Select';
  }

  function getRxTrigger(instance, field, eye) {
    if (field === 'sph') return getSphTrigger(instance, eye);
    return queryEyeControl(
      instance,
      '[data-basari-cl-rx-trigger][data-field="' + field + '"]',
      eye
    );
  }

  function getRxInput(instance, field, eye) {
    if (field === 'sph') return getSphInput(instance, eye);
    return queryEyeControl(
      instance,
      'input[data-basari-cl-rx-field-input][data-field="' + field + '"]',
      eye
    );
  }

  function getRxError(instance, field, eye) {
    var rxError = queryEyeControl(
      instance,
      '[data-basari-cl-rx-error][data-field="' + field + '"]',
      eye
    );
    if (rxError) return rxError;
    if (field === 'sph') return getSphError(instance, eye);
    return queryEyeControl(
      instance,
      '[data-basari-cl-rx-error][data-field="' + field + '"]',
      eye
    );
  }

  function getRxLiveValue(instance, field, eye) {
    if (field === 'sph') return getSphLiveValue(instance, eye);
    var input = getRxInput(instance, field, eye);
    return input ? String(input.value || '').trim() : '';
  }

  function getRxPlaceholder(instance, field, eye) {
    var trigger = getRxTrigger(instance, field, eye);
    if (trigger && trigger.getAttribute('data-placeholder')) {
      return trigger.getAttribute('data-placeholder');
    }
    return getPlaceholderLabel(instance, eye);
  }

  function isCylZero(value) {
    var raw = String(value == null ? '' : value).trim();
    if (!raw) return false;
    if (raw === '0' || raw === '0.0' || raw === '0.00' || raw === '+0.00') return true;
    var n = Number(raw);
    return !isNaN(n) && n === 0;
  }

  function isConfiguredToricAxis(instance, value) {
    var raw = String(value == null ? '' : value).trim();
    if (!raw) return false;
    var cfg = getToricConfig(instance);
    var allowed = buildAxisValues(cfg.axisMin, cfg.axisMax, cfg.axisStep);
    return allowed.indexOf(raw) !== -1;
  }

  function isValidToricAxisSelection(instance, cyl, axis) {
    var cylRaw = String(cyl == null ? '' : cyl).trim();
    var axisRaw = String(axis == null ? '' : axis).trim();
    if (!cylRaw) return false;
    if (isCylZero(cylRaw)) return isCylZero(axisRaw);
    if (!axisRaw || isCylZero(axisRaw)) return false;
    return isConfiguredToricAxis(instance, axisRaw);
  }

  function setAxisDisplay(instance, eye, value) {
    var next = String(value || '').trim();
    var axisInput = getRxInput(instance, 'axis', eye);
    var axisTrigger = getRxTrigger(instance, 'axis', eye);
    var axisLabel = axisTrigger
      ? axisTrigger.querySelector('[data-basari-cl-rx-trigger-label]')
      : null;
    if (axisInput) axisInput.value = next;
    if (axisTrigger) axisTrigger.setAttribute('data-rx-value', next);
    if (axisLabel) {
      axisLabel.textContent = next || getRxPlaceholder(instance, 'axis', eye);
    }
  }

  function setRxLiveValue(instance, field, eye, value, options) {
    if (field === 'sph') {
      setSphLiveValue(instance, eye, value, options);
      return;
    }
    var next = String(value || '').trim();
    var input = getRxInput(instance, field, eye);
    var trigger = getRxTrigger(instance, field, eye);
    var label = trigger ? trigger.querySelector('[data-basari-cl-rx-trigger-label]') : null;
    if (input) input.value = next;
    if (trigger) trigger.setAttribute('data-rx-value', next);
    if (label) label.textContent = next || getRxPlaceholder(instance, field, eye);
    if (next) clearFieldError(instance, field, eye);
    if (field === 'cyl') {
      syncAxisLockForEye(
        instance,
        eye,
        isWithPower(instance) && isEyeChecked(instance, eye),
        { cylChanged: true }
      );
    }
    if (options && options.dispatch) {
      var detail = {
        field: field,
        eye: eye,
        value: next,
        sectionId: instance.config.sectionId
      };
      dispatch(instance.root, 'basari:cl:rx-change', detail);
    }
  }

  function syncAxisLockForEye(instance, eye, rxOn, options) {
    if (!isToricLens(instance)) return;
    options = options || {};
    var cyl = getRxLiveValue(instance, 'cyl', eye);
    var lockedZero = isCylZero(cyl);
    var axisValue = getRxLiveValue(instance, 'axis', eye);
    var cylNonZero = !!cyl && !lockedZero;

    if (rxOn && lockedZero) {
      setAxisDisplay(instance, eye, '0');
      clearFieldError(instance, 'axis', eye);
    } else if (rxOn && cylNonZero && isCylZero(axisValue)) {
      setAxisDisplay(instance, eye, '');
      clearFieldError(instance, 'axis', eye);
    }

    setControlDisabled(getRxTrigger(instance, 'axis', eye), !rxOn || lockedZero);
    setControlDisabled(getRxInput(instance, 'axis', eye), !rxOn);
  }

  function getVisibleRxControl(instance, field, eye) {
    var trigger = getRxTrigger(instance, field, eye);
    if (trigger) return trigger;
    var input = getRxInput(instance, field, eye);
    if (input && input.type !== 'hidden') return input;
    return queryEyeControl(instance, 'select[data-field="' + field + '"]', eye);
  }

  function applyRxErrorState(control, error, invalid) {
    if (control) {
      control.classList.toggle('is-error', !!invalid);
      if (invalid) {
        control.setAttribute('aria-invalid', 'true');
        if (error && error.id) {
          control.setAttribute('aria-describedby', error.id);
        }
      } else {
        control.removeAttribute('aria-invalid');
        control.removeAttribute('aria-describedby');
      }
    }
    if (error) {
      if (invalid) error.removeAttribute('hidden');
      else error.setAttribute('hidden', '');
    }
  }

  function clearFieldError(instance, field, eye) {
    if (field === 'sph') {
      clearSphError(instance, eye);
      return;
    }
    var error = getRxError(instance, field, eye);
    applyRxErrorState(getVisibleRxControl(instance, field, eye), error, false);
  }

  function showFieldError(instance, field, eye, message) {
    if (field === 'sph' && getRxTrigger(instance, 'sph', eye)) {
      showSphError(instance, eye);
      return;
    }
    var error = getRxError(instance, field, eye);
    if (error && !error.id) {
      error.id = 'BasariContactLensRxError-' + field + '-' + String(eye || 'eye');
    }
    if (error && message) error.textContent = message;
    applyRxErrorState(getVisibleRxControl(instance, field, eye), error, true);
  }

  function setSphLiveValue(instance, eye, value, options) {
    var next = String(value || '').trim();
    var input = getSphInput(instance, eye);
    var trigger = getSphTrigger(instance, eye);
    var label = trigger ? trigger.querySelector('[data-basari-cl-sph-trigger-label]') : null;
    if (input) input.value = next;
    if (trigger) trigger.setAttribute('data-sph-value', next);
    if (label) label.textContent = next || getPlaceholderLabel(instance, eye);
    if (next) clearSphError(instance, eye);
    if (options && options.dispatch && input) {
      var detail = {
        field: 'sph',
        eye: eye,
        value: next,
        sectionId: instance.config.sectionId
      };
      dispatch(instance.root, 'basari:cl:rx-change', detail);
      dispatch(instance.root, 'basari:cl:sph-change', detail);
    }
  }

  function clampBoxes(value, maxBoxes) {
    var n = parseInt(value, 10);
    var max = maxBoxes && maxBoxes > 0 ? maxBoxes : 10;
    if (!n || n < 1 || isNaN(n)) return 1;
    if (n > max) return max;
    return n;
  }

  function getEyeBoxesValue(instance, eye) {
    var input = getBoxesInput(instance, eye);
    return clampBoxes(input ? input.value : 1, getSphericalConfig(instance).maxBoxes);
  }

  function isWithPower(instance) {
    if (isSpecialLens(instance)) return true;
    return getPowerMode(instance) === 'with_power';
  }

  function countCheckedEyes(instance) {
    var count = 0;
    var i;
    if (!instance || !instance.dom || !instance.dom.eyeInputs) return 0;
    for (i = 0; i < instance.dom.eyeInputs.length; i += 1) {
      if (instance.dom.eyeInputs[i].checked) count += 1;
    }
    return count;
  }

  function preventLastEyeUncheck(instance, event) {
    var input = event && event.currentTarget;
    if (!input || input.type !== 'checkbox') return;
    if (input.checked && countCheckedEyes(instance) === 1) {
      event.preventDefault();
    }
  }

  function syncSphRowVisibility(instance, hideSph) {
    var rows =
      instance.dom && instance.dom.sphRows && instance.dom.sphRows.length
        ? instance.dom.sphRows
        : instance.root.querySelectorAll('[data-basari-cl-power-row="sph"]');
    var i;
    for (i = 0; i < rows.length; i += 1) {
      if (hideSph) {
        rows[i].setAttribute('hidden', '');
      } else {
        rows[i].removeAttribute('hidden');
      }
    }
  }

  function isEyeActive(instance, eye) {
    return usesSphModal(instance) && isWithPower(instance) && isEyeChecked(instance, eye);
  }

  function setControlDisabled(node, disabled) {
    if (!node) return;
    node.disabled = !!disabled;
    if (disabled) {
      node.setAttribute('aria-disabled', 'true');
      if (node.hasAttribute('data-basari-cl-sph-trigger') || node.hasAttribute('data-basari-cl-rx-trigger')) {
        node.setAttribute('tabindex', '-1');
      }
    } else {
      node.removeAttribute('aria-disabled');
      if (node.hasAttribute('data-basari-cl-sph-trigger') || node.hasAttribute('data-basari-cl-rx-trigger')) {
        node.removeAttribute('tabindex');
      }
    }
  }

  function updateEyeFieldState(instance) {
    if (!instance) return;
    var sphOn;
    var boxesOn;
    var eyes = ['right', 'left'];
    var e;
    var eye;
    var toric = isToricLens(instance);
    var multifocal = isMultifocalLens(instance);
    var special = isSpecialLens(instance);
    for (e = 0; e < eyes.length; e += 1) {
      eye = eyes[e];
      if (special) {
        setControlDisabled(getRxInput(instance, 'sph', eye), false);
        setControlDisabled(getRxInput(instance, 'cyl', eye), false);
        setControlDisabled(getRxInput(instance, 'axis', eye), false);
        setControlDisabled(getRxInput(instance, 'add', eye), false);
        continue;
      }
      boxesOn = isEyeChecked(instance, eye);
      sphOn = isWithPower(instance) && boxesOn;
      setControlDisabled(getSphTrigger(instance, eye), !sphOn);
      setControlDisabled(getSphInput(instance, eye), !sphOn);
      setControlDisabled(getBoxesInput(instance, eye), !boxesOn);
      if (toric) {
        setControlDisabled(getRxTrigger(instance, 'cyl', eye), !sphOn);
        setControlDisabled(getRxInput(instance, 'cyl', eye), !sphOn);
        syncAxisLockForEye(instance, eye, sphOn);
      }
      if (multifocal) {
        setControlDisabled(getRxTrigger(instance, 'add', eye), !sphOn);
        setControlDisabled(getRxInput(instance, 'add', eye), !sphOn);
      }
    }
    syncShopifyQuantity(instance);
  }

  function clearSphError(instance, eye) {
    var trigger = getSphTrigger(instance, eye);
    var select = queryEyeControl(instance, 'select[data-field="sph"]', eye);
    var text = queryEyeControl(instance, 'input[data-basari-cl-special-input][data-field="sph"]', eye);
    var error = getSphError(instance, eye) || getRxError(instance, 'sph', eye);
    applyRxErrorState(trigger, error, false);
    applyRxErrorState(select, error, false);
    applyRxErrorState(text, error, false);
  }

  function clearAllSphErrors(instance) {
    clearSphError(instance, 'right');
    clearSphError(instance, 'left');
  }

  function showSphError(instance, eye) {
    var trigger = getSphTrigger(instance, eye);
    var select = queryEyeControl(instance, 'select[data-field="sph"]', eye);
    var text = queryEyeControl(instance, 'input[data-basari-cl-special-input][data-field="sph"]', eye);
    var error = getSphError(instance, eye) || getRxError(instance, 'sph', eye);
    var control = trigger || select || text;
    if (error && !error.id) {
      error.id = 'BasariContactLensSphError-' + String(eye || 'eye');
    }
    applyRxErrorState(control, error, true);
  }

  function calculateShopifyQuantity(instance) {
    if (isSpecialLens(instance) || !usesBoxQuantity(instance)) return 1;
    var qty = 0;
    if (isEyeChecked(instance, 'right')) qty += getEyeBoxesValue(instance, 'right');
    if (isEyeChecked(instance, 'left')) qty += getEyeBoxesValue(instance, 'left');
    if (qty < 1) return 1;
    return qty;
  }

  function syncShopifyQuantity(instance) {
    var form = instance && instance.dom ? instance.dom.form : null;
    var qty = calculateShopifyQuantity(instance);
    if (instance.dom && instance.dom.quantityInput) {
      instance.dom.quantityInput.value = String(qty);
    }
    if (form) setFormNamedValue(form, 'quantity', String(qty));
    return qty;
  }

  function syncPrescriptionForPurchase(instance) {
    var eyes = ['right', 'left'];
    var e;
    var withPower = isWithPower(instance);
    var boxQty = usesBoxQuantity(instance);
    var toric = isToricLens(instance);
    var multifocal = isMultifocalLens(instance);
    var special = isSpecialLens(instance);
    var specialFields = ['sph', 'cyl', 'axis', 'add'];
    var f;

    for (e = 0; e < eyes.length; e += 1) {
      var eye = eyes[e];
      if (special) {
        for (f = 0; f < specialFields.length; f += 1) {
          var specialInput = getRxInput(instance, specialFields[f], eye);
          if (specialInput) {
            specialInput.disabled = !String(specialInput.value || '').trim();
          }
        }
        continue;
      }
      var eyeOn = boxQty && isEyeChecked(instance, eye);
      var sphActive = eyeOn && withPower;
      var sphInput = getSphInput(instance, eye);
      var boxesInput = getBoxesInput(instance, eye);
      if (sphInput) {
        sphInput.disabled = !sphActive;
      }
      if (boxesInput) {
        boxesInput.disabled = !eyeOn;
      }
      if (toric) {
        var cylInput = getRxInput(instance, 'cyl', eye);
        var axisInput = getRxInput(instance, 'axis', eye);
        if (cylInput) {
          cylInput.disabled = !sphActive;
        }
        if (axisInput) {
          if (sphActive && isCylZero(getRxLiveValue(instance, 'cyl', eye))) {
            axisInput.value = '0';
          }
          axisInput.disabled = !sphActive;
        }
      }
      if (multifocal) {
        var addInput = getRxInput(instance, 'add', eye);
        if (addInput) {
          addInput.disabled = !sphActive;
        }
      }
    }

    return syncShopifyQuantity(instance);
  }

  function restoreEyeFieldState(instance) {
    updateEyeFieldState(instance);
  }

  function validateSphericalPrescription(instance) {
    if (!isSphericalLens(instance)) return true;

    if (countCheckedEyes(instance) < 1) {
      clearAllSphErrors(instance);
      syncEyeWarning(instance);
      return false;
    }

    if (!isWithPower(instance)) {
      clearAllSphErrors(instance);
      syncEyeWarning(instance);
      return true;
    }

    syncEyeWarning(instance);

    var rightOn = isEyeChecked(instance, 'right');
    var leftOn = isEyeChecked(instance, 'left');
    var valid = true;
    if (rightOn && !getSphLiveValue(instance, 'right')) {
      showSphError(instance, 'right');
      valid = false;
    } else {
      clearSphError(instance, 'right');
    }
    if (leftOn && !getSphLiveValue(instance, 'left')) {
      showSphError(instance, 'left');
      valid = false;
    } else {
      clearSphError(instance, 'left');
    }

    if (!valid) {
      var firstError =
        instance.root.querySelector('[data-basari-cl-sph-trigger].is-error') ||
        instance.root.querySelector('select[data-field="sph"].is-error');
      if (firstError && typeof firstError.scrollIntoView === 'function') {
        try {
          firstError.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } catch (error) {
          /* ignore */
        }
      }
    }

    return valid;
  }

  function clearToricErrors(instance) {
    clearAllSphErrors(instance);
    clearFieldError(instance, 'cyl', 'right');
    clearFieldError(instance, 'cyl', 'left');
    clearFieldError(instance, 'axis', 'right');
    clearFieldError(instance, 'axis', 'left');
  }

  function scrollToFirstRxError(instance) {
    var firstError =
      instance.root.querySelector('[data-basari-cl-sph-trigger].is-error') ||
      instance.root.querySelector('[data-basari-cl-rx-trigger].is-error') ||
      instance.root.querySelector('input[data-basari-cl-special-input].is-error') ||
      instance.root.querySelector('select[data-field="sph"].is-error');
    if (firstError && typeof firstError.scrollIntoView === 'function') {
      try {
        firstError.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } catch (error) {
        /* ignore */
      }
    }
  }

  function validateToricPrescription(instance) {
    if (!isToricLens(instance)) return true;

    if (countCheckedEyes(instance) < 1) {
      clearToricErrors(instance);
      syncEyeWarning(instance);
      return false;
    }

    syncEyeWarning(instance);

    var eyes = ['right', 'left'];
    var valid = true;
    var e;
    for (e = 0; e < eyes.length; e += 1) {
      var eye = eyes[e];
      if (!isEyeChecked(instance, eye)) {
        clearFieldError(instance, 'sph', eye);
        clearFieldError(instance, 'cyl', eye);
        clearFieldError(instance, 'axis', eye);
        continue;
      }

      if (!getSphLiveValue(instance, eye)) {
        showFieldError(instance, 'sph', eye);
        valid = false;
      } else {
        clearFieldError(instance, 'sph', eye);
      }

      var cyl = getRxLiveValue(instance, 'cyl', eye);
      var axis = getRxLiveValue(instance, 'axis', eye);
      if (!cyl) {
        showFieldError(instance, 'cyl', eye);
        valid = false;
      } else {
        clearFieldError(instance, 'cyl', eye);
        if (isCylZero(cyl)) {
          syncAxisLockForEye(instance, eye, true);
          clearFieldError(instance, 'axis', eye);
        } else if (!isValidToricAxisSelection(instance, cyl, axis)) {
          showFieldError(instance, 'axis', eye);
          valid = false;
        } else {
          clearFieldError(instance, 'axis', eye);
        }
      }
    }

    if (!valid) scrollToFirstRxError(instance);
    return valid;
  }

  function clearMultifocalErrors(instance) {
    clearAllSphErrors(instance);
    clearFieldError(instance, 'add', 'right');
    clearFieldError(instance, 'add', 'left');
  }

  function validateMultifocalPrescription(instance) {
    if (!isMultifocalLens(instance)) return true;

    if (countCheckedEyes(instance) < 1) {
      clearMultifocalErrors(instance);
      syncEyeWarning(instance);
      return false;
    }

    syncEyeWarning(instance);

    var eyes = ['right', 'left'];
    var valid = true;
    var e;
    for (e = 0; e < eyes.length; e += 1) {
      var eye = eyes[e];
      if (!isEyeChecked(instance, eye)) {
        clearFieldError(instance, 'sph', eye);
        clearFieldError(instance, 'add', eye);
        continue;
      }

      if (!getSphLiveValue(instance, eye)) {
        showFieldError(instance, 'sph', eye);
        valid = false;
      } else {
        clearFieldError(instance, 'sph', eye);
      }

      if (!getRxLiveValue(instance, 'add', eye)) {
        showFieldError(instance, 'add', eye);
        valid = false;
      } else {
        clearFieldError(instance, 'add', eye);
      }
    }

    if (!valid) scrollToFirstRxError(instance);
    return valid;
  }

  function getSpecialString(instance, key, fallback) {
    var strings =
      instance && instance.config && instance.config.specialStrings
        ? instance.config.specialStrings
        : {};
    var value = strings[key];
    return value ? String(value) : fallback;
  }

  function getSpecialFieldRange(instance, field) {
    var cfg = getSpecialConfig(instance);
    if (field === 'sph') {
      return { min: cfg.sphereMin, max: cfg.sphereMax, step: cfg.sphereStep, kind: 'power' };
    }
    if (field === 'cyl') {
      return { min: cfg.cylinderMin, max: cfg.cylinderMax, step: cfg.cylinderStep, kind: 'power' };
    }
    if (field === 'add') {
      return { min: cfg.addMin, max: cfg.addMax, step: cfg.addStep, kind: 'power' };
    }
    return { min: cfg.axisMin, max: cfg.axisMax, step: cfg.axisStep, kind: 'axis' };
  }

  function parseSpecialRawValue(raw) {
    var text = String(raw == null ? '' : raw).trim().replace(/\s+/g, '');
    if (!text) return { empty: true };
    if (!/^[+-]?\d+(\.\d+)?$/.test(text)) return { invalid: true };
    var number = Number(text);
    if (!isFinite(number)) return { invalid: true };
    return {
      empty: false,
      invalid: false,
      number: number,
      hundredths: Math.round(number * 100)
    };
  }

  function formatSpecialPowerValue(number) {
    return formatSphValue(Math.round(Number(number) * 100));
  }

  function formatSpecialRangeBound(range, value) {
    if (!range || range.kind === 'axis') return String(value);
    return formatSpecialPowerValue(value);
  }

  function evaluateSpecialFieldValue(instance, field, raw) {
    var parsed = parseSpecialRawValue(raw);
    var range = getSpecialFieldRange(instance, field);
    var stepHundredths = Math.round(Number(range.step) * 100) || 1;
    var minHundredths = Math.round(Number(range.min) * 100);
    var maxHundredths = Math.round(Number(range.max) * 100);
    if (parsed.empty) return { empty: true };
    if (parsed.invalid) {
      return {
        empty: false,
        valid: false,
        message: getSpecialString(instance, 'invalidValue', 'Invalid value')
      };
    }
    if (range.kind === 'axis') {
      if (parsed.hundredths % 100 !== 0) {
        return {
          empty: false,
          valid: false,
          message: getSpecialString(instance, 'invalidValue', 'Invalid value')
        };
      }
      if (parsed.number < range.min || parsed.number > range.max) {
        return {
          empty: false,
          valid: false,
          message: getSpecialString(instance, 'valueRange', 'Value must be between __MIN__ and __MAX__')
            .replace('__MIN__', formatSpecialRangeBound(range, range.min))
            .replace('__MAX__', formatSpecialRangeBound(range, range.max))
        };
      }
      return { empty: false, valid: true, display: String(Math.round(parsed.number)) };
    }
    if (parsed.hundredths % stepHundredths !== 0) {
      return {
        empty: false,
        valid: false,
        message: getSpecialString(instance, 'invalidValue', 'Invalid value')
      };
    }
    if (parsed.hundredths < minHundredths || parsed.hundredths > maxHundredths) {
      return {
        empty: false,
        valid: false,
        message: getSpecialString(instance, 'valueRange', 'Value must be between __MIN__ and __MAX__')
          .replace('__MIN__', formatSpecialRangeBound(range, range.min))
          .replace('__MAX__', formatSpecialRangeBound(range, range.max))
      };
    }
    return { empty: false, valid: true, display: formatSpecialPowerValue(parsed.number) };
  }

  function isSpecialEyeActive(instance, eye) {
    var fields = ['sph', 'cyl', 'axis', 'add'];
    var i;
    for (i = 0; i < fields.length; i += 1) {
      if (getRxLiveValue(instance, fields[i], eye)) return true;
    }
    return false;
  }

  function setSpecialEyeWarning(instance, show) {
    if (!instance.dom.warning) return;
    if (show) instance.dom.warning.removeAttribute('hidden');
    else instance.dom.warning.setAttribute('hidden', '');
  }

  function clearSpecialEyeErrors(instance, eye) {
    clearFieldError(instance, 'sph', eye);
    clearFieldError(instance, 'cyl', eye);
    clearFieldError(instance, 'axis', eye);
    clearFieldError(instance, 'add', eye);
  }

  function normalizeSpecialTextInput(instance, input) {
    if (!input || !isSpecialLens(instance)) return;
    var field = input.getAttribute('data-field');
    var result = evaluateSpecialFieldValue(instance, field, input.value);
    if (result.empty) {
      clearFieldError(instance, field, input.getAttribute('data-eye'));
      return;
    }
    if (result.valid && result.display) {
      input.value = result.display;
      clearFieldError(instance, field, input.getAttribute('data-eye'));
      return;
    }
    showFieldError(instance, field, input.getAttribute('data-eye'), result.message);
  }

  function refreshSpecialFieldError(instance, input) {
    if (!input || !isSpecialLens(instance)) return;
    var field = input.getAttribute('data-field');
    var eye = input.getAttribute('data-eye');
    var result = evaluateSpecialFieldValue(instance, field, input.value);
    if (result.empty || result.valid) {
      clearFieldError(instance, field, eye);
    }
  }

  function validateSpecialEye(instance, eye) {
    var sphRaw = getRxLiveValue(instance, 'sph', eye);
    var cylRaw = getRxLiveValue(instance, 'cyl', eye);
    var axisRaw = getRxLiveValue(instance, 'axis', eye);
    var addRaw = getRxLiveValue(instance, 'add', eye);
    var sph = evaluateSpecialFieldValue(instance, 'sph', sphRaw);
    var cyl = evaluateSpecialFieldValue(instance, 'cyl', cylRaw);
    var axis = evaluateSpecialFieldValue(instance, 'axis', axisRaw);
    var add = evaluateSpecialFieldValue(instance, 'add', addRaw);
    var valid = true;
    var cylIsZero = cyl.valid && isCylZero(cyl.display);
    var cylIsNonZero = cyl.valid && !cylIsZero;

    if (sph.empty) {
      showFieldError(
        instance,
        'sph',
        eye,
        getSpecialString(instance, 'enterSphere', 'Enter Sphere')
      );
      valid = false;
    } else if (!sph.valid) {
      showFieldError(instance, 'sph', eye, sph.message);
      valid = false;
    } else {
      clearFieldError(instance, 'sph', eye);
    }

    if (cyl.empty) {
      clearFieldError(instance, 'cyl', eye);
    } else if (!cyl.valid) {
      showFieldError(instance, 'cyl', eye, cyl.message);
      valid = false;
    } else {
      clearFieldError(instance, 'cyl', eye);
    }

    if (cylIsNonZero && axis.empty) {
      showFieldError(
        instance,
        'axis',
        eye,
        getSpecialString(instance, 'enterAxis', 'Enter Axis')
      );
      valid = false;
    } else if (axis.empty) {
      clearFieldError(instance, 'axis', eye);
    } else if (!axis.valid) {
      showFieldError(instance, 'axis', eye, axis.message);
      valid = false;
    } else {
      clearFieldError(instance, 'axis', eye);
    }

    if (add.empty) {
      clearFieldError(instance, 'add', eye);
    } else if (!add.valid) {
      showFieldError(instance, 'add', eye, add.message);
      valid = false;
    } else {
      clearFieldError(instance, 'add', eye);
    }

    return valid;
  }

  function validateSpecialPrescription(instance) {
    if (!isSpecialLens(instance)) return true;

    var rightActive = isSpecialEyeActive(instance, 'right');
    var leftActive = isSpecialEyeActive(instance, 'left');
    var valid = true;

    if (!rightActive && !leftActive) {
      clearSpecialEyeErrors(instance, 'right');
      clearSpecialEyeErrors(instance, 'left');
      setSpecialEyeWarning(instance, true);
      if (instance.dom.warning && typeof instance.dom.warning.scrollIntoView === 'function') {
        try {
          instance.dom.warning.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } catch (error) {
          /* ignore */
        }
      }
      return false;
    }

    setSpecialEyeWarning(instance, false);

    if (rightActive) {
      if (!validateSpecialEye(instance, 'right')) valid = false;
    } else {
      clearSpecialEyeErrors(instance, 'right');
    }

    if (leftActive) {
      if (!validateSpecialEye(instance, 'left')) valid = false;
    } else {
      clearSpecialEyeErrors(instance, 'left');
    }

    if (!valid) scrollToFirstRxError(instance);
    return valid;
  }

  function bindSpecialTextInputs(instance) {
    if (!isSpecialLens(instance) || !instance.root) return;
    var inputs = instance.root.querySelectorAll('[data-basari-cl-special-input]');
    var i;
    for (i = 0; i < inputs.length; i += 1) {
      inputs[i].addEventListener('blur', function (event) {
        normalizeSpecialTextInput(instance, event.currentTarget);
      });
      inputs[i].addEventListener('input', function (event) {
        refreshSpecialFieldError(instance, event.currentTarget);
        if (isSpecialEyeActive(instance, 'right') || isSpecialEyeActive(instance, 'left')) {
          setSpecialEyeWarning(instance, false);
        }
      });
    }
  }

  function validateContactLensPrescription(instance) {
    if (isSpecialLens(instance)) return validateSpecialPrescription(instance);
    if (isToricLens(instance)) return validateToricPrescription(instance);
    if (isMultifocalLens(instance)) return validateMultifocalPrescription(instance);
    return validateSphericalPrescription(instance);
  }

  function prepareContactLensPurchase(instance) {
    if (!validateContactLensPrescription(instance)) return false;
    syncPrescriptionForPurchase(instance);
    return true;
  }

  function clearOneTimeSellingPlan(form) {
    if (!form) return;
    var plans = getFormNamedControls(form, 'selling_plan');
    var i;
    for (i = 0; i < plans.length; i += 1) {
      plans[i].value = '';
      plans[i].disabled = true;
    }
  }

  function setOneTimePurchaseFields(form) {
    clearOneTimeSellingPlan(form);
    setFormNamedValue(form, 'return_to', getCheckoutReturnTo());
  }

  function buyNowContactLens(instance) {
    var form = instance && instance.dom ? instance.dom.form : null;
    var btn = instance && instance.dom ? instance.dom.buyNow : null;
    if (!form) return;
    if (!prepareContactLensPurchase(instance)) return;

    setOneTimePurchaseFields(form);
    if (btn) {
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
    }

    form.addEventListener(
      'submit',
      function (event) {
        if (!event.defaultPrevented || !btn) return;
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        restoreEyeFieldState(instance);
      },
      { once: true }
    );

    try {
      submitCartFormPreservingListeners(form);
    } catch (error) {
      if (btn) {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
      }
      restoreEyeFieldState(instance);
    }
  }

  function getContactLensCartDrawer() {
    return document.querySelector('cart-drawer');
  }

  function isContactLensCartBusy(button) {
    if (!button) return false;
    return (
      button.disabled ||
      button.getAttribute('aria-busy') === 'true' ||
      button.classList.contains('is-loading')
    );
  }

  function setContactLensCartLoading(button, loading) {
    if (!button) return;
    button.classList.toggle('is-loading', !!loading);
    if (loading) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    } else {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * Snapshot the purchase form after validation has enabled only the
   * configured eyes. Disabled OD/OS fields stay out of the payload.
   * return_to is omitted so this add does not follow Buy Now to checkout.
   */
  function buildContactLensCartFormData(form, cart) {
    var formData = new FormData(form);
    formData.delete('return_to');
    if (cart && typeof cart.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cart
          .getSectionsToRender()
          .map(function (section) {
            return section.id;
          })
          .join(',')
      );
      formData.append('sections_url', window.location.pathname);
      if (typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(document.activeElement);
      }
    }
    return formData;
  }

  function publishContactLensCartUpdate(variantId, payload) {
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'basari-contact-lens',
        productVariantId: variantId,
        cartData: payload
      });
    }
  }

  function getPremiumCart() {
    return window.PremiumCart &&
      typeof window.PremiumCart.add === 'function' &&
      document.getElementById('pdc-cart-drawer')
      ? window.PremiumCart
      : null;
  }

  /**
   * Legacy fallback only (used when the Premium Cart Drawer section is not on
   * the page): open the Dawn drawer with the sections returned by /cart/add.
   */
  function openContactLensCartDrawer(cart, payload) {
    if (cart && typeof cart.renderContents === 'function' && payload && payload.sections) {
      cart.renderContents(payload);
      return true;
    }
    return false;
  }

  /**
   * Premium path: ONE /cart/add.js request made by PremiumCart.add(), which
   * renders the drawer from the same response and opens it. Quantity is the
   * sum of the active eyes' boxes (syncShopifyQuantity), and only the active
   * eyes' properties are in the payload (disabled inputs are skipped).
   */
  function addContactLensToCartPremium(instance, premium, form, btn) {
    var formData;
    try {
      formData = buildContactLensCartFormData(form, null);
    } finally {
      restoreEyeFieldState(instance);
    }

    instance._cartAddPending = true;
    setContactLensCartLoading(btn, true);

    premium
      .add(formData, { trigger: btn, source: 'basari-contact-lens' })
      .catch(function () {
        /* PremiumCart shows the Shopify error message inside the drawer. */
      })
      .then(function () {
        instance._cartAddPending = false;
        setContactLensCartLoading(btn, false);
      });
  }

  /**
   * Cart icon: same validation, quantity, and line-item properties as Buy Now.
   * Uses the Premium Cart Drawer when present; otherwise posts with the Ajax
   * Cart API and opens the Dawn drawer.
   */
  function addContactLensToCart(instance) {
    var form = instance && instance.dom ? instance.dom.form : null;
    var btn = instance && instance.dom ? instance.dom.addToCart : null;
    var cart;
    var formData;
    var variantId;
    var routes;
    var cartAddUrl;
    var config;

    if (!form || !btn || instance._cartAddPending || isContactLensCartBusy(btn)) return;
    if (!prepareContactLensPurchase(instance)) return;

    if (instance.root) {
      syncVariantFormInput(instance, instance.root.getAttribute('data-variant-id'));
    }
    clearOneTimeSellingPlan(form);

    var premium = getPremiumCart();
    if (premium) {
      addContactLensToCartPremium(instance, premium, form, btn);
      return;
    }

    cart = getContactLensCartDrawer();
    try {
      formData = buildContactLensCartFormData(form, cart);
      variantId = formData.get('id');
    } finally {
      restoreEyeFieldState(instance);
    }

    instance._cartAddPending = true;
    setContactLensCartLoading(btn, true);

    routes = window.routes || {};
    cartAddUrl = routes.cart_add_url || '/cart/add';
    config =
      typeof window.fetchConfig === 'function'
        ? window.fetchConfig('javascript')
        : {
            method: 'POST',
            headers: { Accept: 'application/javascript' }
          };
    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    fetch(cartAddUrl, config)
      .then(function (response) {
        return response.json().then(function (payload) {
          return { ok: response.ok, payload: payload };
        });
      })
      .then(function (result) {
        var payload = result && result.payload;
        if (!result || !result.ok || (payload && payload.status)) {
          return;
        }
        publishContactLensCartUpdate(variantId, payload);
        if (!openContactLensCartDrawer(cart, payload) && routes.cart_url) {
          window.location = routes.cart_url;
        }
      })
      .catch(function () {
        /* Leave the configured PDP in place. Validation errors are shown earlier. */
      })
      .finally(function () {
        instance._cartAddPending = false;
        setContactLensCartLoading(btn, false);
      });
  }

  function getSphModal(instance) {
    return instance && instance.dom ? instance.dom.sphModal : null;
  }

  function createSphOption(value) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'basari-cl-sph-modal__option';
    btn.setAttribute('role', 'radio');
    btn.setAttribute('data-basari-cl-sph-option', '');
    btn.setAttribute('data-sph-value', value);
    btn.setAttribute('aria-checked', 'false');
    btn.setAttribute('tabindex', '-1');

    var radio = document.createElement('span');
    radio.className = 'basari-cl-sph-modal__radio';
    radio.setAttribute('aria-hidden', 'true');

    var text = document.createElement('span');
    text.className = 'basari-cl-sph-modal__option-label';
    text.textContent = value;

    btn.appendChild(radio);
    btn.appendChild(text);
    return btn;
  }

  function populateSphList(list, values) {
    if (!list) return;
    list.innerHTML = '';
    var i;
    for (i = 0; i < values.length; i += 1) {
      list.appendChild(createSphOption(formatSphValue(values[i])));
    }
  }

  function getSphRadioOptions(modal) {
    if (!modal) return [];
    var nodes = modal.querySelectorAll('[data-basari-cl-sph-option]');
    var list = [];
    var i;
    for (i = 0; i < nodes.length; i += 1) list.push(nodes[i]);
    return list;
  }

  function highlightSphModalValue(modal, value) {
    if (!modal) return null;
    var options = getSphRadioOptions(modal);
    var i;
    var matched = null;
    for (i = 0; i < options.length; i += 1) {
      var selected = options[i].getAttribute('data-sph-value') === value;
      options[i].classList.toggle('basari-cl-sph-modal__option--selected', selected);
      options[i].setAttribute('aria-checked', selected ? 'true' : 'false');
      options[i].setAttribute('tabindex', selected ? '0' : '-1');
      if (selected) matched = options[i];
    }
    if (!matched && options.length) {
      options[0].setAttribute('tabindex', '0');
    }
    if (matched && typeof matched.scrollIntoView === 'function') {
      try {
        matched.scrollIntoView({ block: 'nearest' });
      } catch (error) {
        /* ignore */
      }
    }
    return matched || options[0] || null;
  }

  function moveSphRadioFocus(modal, current, delta) {
    var options = getSphRadioOptions(modal);
    if (!options.length) return;
    var index = current ? options.indexOf(current) : -1;
    if (index < 0) {
      index = 0;
      var i;
      for (i = 0; i < options.length; i += 1) {
        if (options[i].getAttribute('tabindex') === '0') {
          index = i;
          break;
        }
      }
    }
    var nextIndex = (index + delta + options.length) % options.length;
    var next = options[nextIndex];
    var j;
    for (j = 0; j < options.length; j += 1) {
      options[j].setAttribute('tabindex', options[j] === next ? '0' : '-1');
    }
    if (next && typeof next.focus === 'function') next.focus();
  }

  function closeSphModal(instance, options) {
    var modal = getSphModal(instance);
    if (!modal || modal.hasAttribute('hidden')) return;
    modal.setAttribute('hidden', '');
    document.documentElement.classList.remove(SPH_MODAL_BODY_CLASS);
    document.body.classList.remove(SPH_MODAL_BODY_CLASS);

    var eye = modal.getAttribute('data-active-eye') || '';
    var trigger = getSphTrigger(instance, eye);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    if (modal._basariKeyHandler) {
      document.removeEventListener('keydown', modal._basariKeyHandler, true);
      modal._basariKeyHandler = null;
    }

    var restoreFocus = !options || options.restoreFocus !== false;
    var returnFocus = modal._basariReturnFocus || trigger;
    modal._basariReturnFocus = null;
    if (restoreFocus && returnFocus && typeof returnFocus.focus === 'function') {
      window.setTimeout(function () {
        returnFocus.focus();
      }, 0);
    }
  }

  function bindSphModal(instance, modal) {
    if (!modal || modal.getAttribute('data-basari-cl-sph-bound') === 'true') return;
    modal.setAttribute('data-basari-cl-sph-bound', 'true');

    var backdrop = modal.querySelector('[data-basari-cl-sph-backdrop]');
    var closeBtn = modal.querySelector('[data-basari-cl-sph-close]');

    function onDismiss() {
      closeSphModal(instance);
    }

    if (backdrop) backdrop.addEventListener('click', onDismiss);
    if (closeBtn) closeBtn.addEventListener('click', onDismiss);

    modal.addEventListener('click', function (event) {
      var option = event.target.closest ? event.target.closest('[data-basari-cl-sph-option]') : null;
      if (!option || !modal.contains(option)) return;
      var eye = modal.getAttribute('data-active-eye') || '';
      var value = option.getAttribute('data-sph-value') || '';
      if (!eye || !value) return;
      setSphLiveValue(instance, eye, value, { dispatch: true });
      closeSphModal(instance);
    });
  }

  function openSphModal(instance, eye) {
    var modal = getSphModal(instance);
    if (!modal || !eye || !isEyeActive(instance, eye)) return;

    closeAllRxSelectModals(instance, { restoreFocus: false });

    bindSphModal(instance, modal);

    var title = modal.querySelector('[data-basari-cl-sph-title]');
    var titleText =
      eye === 'left'
        ? modal.getAttribute('data-title-left')
        : modal.getAttribute('data-title-right');
    if (title && titleText) title.textContent = titleText;

    modal.setAttribute('data-active-eye', eye);

    var trigger = getSphTrigger(instance, eye);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    modal._basariReturnFocus = document.activeElement;
    modal.removeAttribute('hidden');
    document.documentElement.classList.add(SPH_MODAL_BODY_CLASS);
    document.body.classList.add(SPH_MODAL_BODY_CLASS);

    var dialog = modal.querySelector('[data-basari-cl-sph-dialog]');
    window.setTimeout(function () {
      var selected = highlightSphModalValue(modal, getSphLiveValue(instance, eye));
      var focusNode = selected || dialog;
      if (focusNode && typeof focusNode.focus === 'function') focusNode.focus();
    }, 0);

    if (!modal._basariKeyHandler) {
      modal._basariKeyHandler = function (event) {
        if (modal.hasAttribute('hidden')) return;
        var key = event.key;
        if (key === 'Escape' || event.keyCode === 27) {
          event.preventDefault();
          closeSphModal(instance);
          return;
        }
        if (key === 'Tab') {
          var focusables = getFocusable(dialog || modal);
          if (!focusables.length) return;
          var first = focusables[0];
          var last = focusables[focusables.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
          return;
        }
        var option =
          event.target && event.target.closest
            ? event.target.closest('[data-basari-cl-sph-option]')
            : null;
        if (!option || !modal.contains(option)) return;
        if (key === 'ArrowDown' || key === 'ArrowRight') {
          event.preventDefault();
          moveSphRadioFocus(modal, option, 1);
          return;
        }
        if (key === 'ArrowUp' || key === 'ArrowLeft') {
          event.preventDefault();
          moveSphRadioFocus(modal, option, -1);
          return;
        }
        if (key === 'Home') {
          event.preventDefault();
          moveSphRadioFocus(modal, option, -getSphRadioOptions(modal).indexOf(option));
          return;
        }
        if (key === 'End') {
          event.preventDefault();
          var radios = getSphRadioOptions(modal);
          moveSphRadioFocus(modal, option, radios.length - 1 - radios.indexOf(option));
        }
      };
      document.addEventListener('keydown', modal._basariKeyHandler, true);
    }
  }

  function loadSphOptions(instance) {
    if (!usesSphModal(instance)) return;
    var modal = getSphModal(instance);
    if (!modal) return;
    if (modal.getAttribute('data-basari-cl-sph-ready') === 'true') return;

    var cfg = getSphericalConfig(instance);
    populateSphList(
      modal.querySelector('[data-basari-cl-sph-list="negative"]'),
      buildSphHundredths(cfg.negativeMin, cfg.negativeMax, cfg.step)
    );
    populateSphList(
      modal.querySelector('[data-basari-cl-sph-list="positive"]'),
      buildSphHundredths(cfg.positiveMin, cfg.positiveMax, cfg.step)
    );
    modal.setAttribute('data-basari-cl-sph-ready', 'true');
  }

  function getRxSelectModal(instance, field) {
    if (!instance || !instance.root || !field) return null;
    return instance.root.querySelector('[data-basari-cl-rx-modal][data-field="' + field + '"]');
  }

  function createRxOption(value, displayLabel) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'basari-cl-sph-modal__option';
    btn.setAttribute('role', 'radio');
    btn.setAttribute('data-basari-cl-rx-option', '');
    btn.setAttribute('data-rx-value', value);
    btn.setAttribute('aria-checked', 'false');
    btn.setAttribute('tabindex', '-1');

    var radio = document.createElement('span');
    radio.className = 'basari-cl-sph-modal__radio';
    radio.setAttribute('aria-hidden', 'true');

    var text = document.createElement('span');
    text.className = 'basari-cl-sph-modal__option-label';
    text.textContent = displayLabel || value;

    btn.appendChild(radio);
    btn.appendChild(text);
    return btn;
  }

  function formatRxOptionLabel(item) {
    if (!item || typeof item !== 'object') return String(item == null ? '' : item);
    var label = item.label || item.value || '';
    var range = item.range || '';
    if (!range) return label;
    return label + ' \u2013 (' + range + ')';
  }

  function populateRxList(list, values) {
    if (!list) return;
    list.innerHTML = '';
    var i;
    for (i = 0; i < values.length; i += 1) {
      var item = values[i];
      if (item && typeof item === 'object') {
        list.appendChild(createRxOption(item.value, formatRxOptionLabel(item)));
      } else {
        list.appendChild(createRxOption(String(item)));
      }
    }
  }

  function getRxRadioOptions(modal) {
    if (!modal) return [];
    var nodes = modal.querySelectorAll('[data-basari-cl-rx-option]');
    var list = [];
    var i;
    for (i = 0; i < nodes.length; i += 1) list.push(nodes[i]);
    return list;
  }

  function highlightRxModalValue(modal, value) {
    if (!modal) return null;
    var options = getRxRadioOptions(modal);
    var i;
    var matched = null;
    for (i = 0; i < options.length; i += 1) {
      var selected = options[i].getAttribute('data-rx-value') === value;
      options[i].classList.toggle('basari-cl-sph-modal__option--selected', selected);
      options[i].setAttribute('aria-checked', selected ? 'true' : 'false');
      options[i].setAttribute('tabindex', selected ? '0' : '-1');
      if (selected) matched = options[i];
    }
    if (!matched && options.length) {
      options[0].setAttribute('tabindex', '0');
    }
    if (matched && typeof matched.scrollIntoView === 'function') {
      try {
        matched.scrollIntoView({ block: 'nearest' });
      } catch (error) {
        /* ignore */
      }
    }
    return matched || options[0] || null;
  }

  function moveRxRadioFocus(modal, current, delta) {
    var options = getRxRadioOptions(modal);
    if (!options.length) return;
    var index = current ? options.indexOf(current) : -1;
    if (index < 0) {
      index = 0;
      var i;
      for (i = 0; i < options.length; i += 1) {
        if (options[i].getAttribute('tabindex') === '0') {
          index = i;
          break;
        }
      }
    }
    var nextIndex = (index + delta + options.length) % options.length;
    var next = options[nextIndex];
    var j;
    for (j = 0; j < options.length; j += 1) {
      options[j].setAttribute('tabindex', options[j] === next ? '0' : '-1');
    }
    if (next && typeof next.focus === 'function') next.focus();
  }

  function closeRxSelectModal(instance, field, options) {
    var modal = getRxSelectModal(instance, field);
    if (!modal || modal.hasAttribute('hidden')) return;
    modal.setAttribute('hidden', '');
    document.documentElement.classList.remove(SPH_MODAL_BODY_CLASS);
    document.body.classList.remove(SPH_MODAL_BODY_CLASS);

    var eye = modal.getAttribute('data-active-eye') || '';
    var trigger = getRxTrigger(instance, field, eye);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    if (modal._basariKeyHandler) {
      document.removeEventListener('keydown', modal._basariKeyHandler, true);
      modal._basariKeyHandler = null;
    }

    var restoreFocus = !options || options.restoreFocus !== false;
    var returnFocus = modal._basariReturnFocus || trigger;
    modal._basariReturnFocus = null;
    if (restoreFocus && returnFocus && typeof returnFocus.focus === 'function') {
      window.setTimeout(function () {
        returnFocus.focus();
      }, 0);
    }
  }

  function closeAllRxSelectModals(instance, options) {
    closeRxSelectModal(instance, 'cyl', options);
    closeRxSelectModal(instance, 'axis', options);
    closeRxSelectModal(instance, 'add', options);
  }

  function bindRxSelectModal(instance, modal, field) {
    if (!modal || modal.getAttribute('data-basari-cl-rx-bound') === 'true') return;
    modal.setAttribute('data-basari-cl-rx-bound', 'true');

    var backdrop = modal.querySelector('[data-basari-cl-rx-backdrop]');
    var closeBtn = modal.querySelector('[data-basari-cl-rx-close]');

    function onDismiss() {
      closeRxSelectModal(instance, field);
    }

    if (backdrop) backdrop.addEventListener('click', onDismiss);
    if (closeBtn) closeBtn.addEventListener('click', onDismiss);

    modal.addEventListener('click', function (event) {
      var option = event.target.closest ? event.target.closest('[data-basari-cl-rx-option]') : null;
      if (!option || !modal.contains(option)) return;
      var eye = modal.getAttribute('data-active-eye') || '';
      var value = option.getAttribute('data-rx-value') || '';
      if (!eye || value === '') return;
      setRxLiveValue(instance, field, eye, value, { dispatch: true });
      closeRxSelectModal(instance, field);
    });
  }

  function canOpenRxSelectModal(instance, field, eye) {
    if (!field || !eye || !isEyeActive(instance, eye)) return false;
    if (isToricLens(instance) && (field === 'cyl' || field === 'axis')) {
      if (field === 'axis' && isCylZero(getRxLiveValue(instance, 'cyl', eye))) return false;
      return true;
    }
    if (isMultifocalLens(instance) && field === 'add') return true;
    return false;
  }

  function openRxSelectModal(instance, field, eye) {
    if (!canOpenRxSelectModal(instance, field, eye)) return;

    var modal = getRxSelectModal(instance, field);
    if (!modal) return;

    closeSphModal(instance, { restoreFocus: false });
    if (field !== 'cyl') closeRxSelectModal(instance, 'cyl', { restoreFocus: false });
    if (field !== 'axis') closeRxSelectModal(instance, 'axis', { restoreFocus: false });
    if (field !== 'add') closeRxSelectModal(instance, 'add', { restoreFocus: false });

    bindRxSelectModal(instance, modal, field);

    var title = modal.querySelector('[data-basari-cl-rx-title]');
    var titleText =
      eye === 'left'
        ? modal.getAttribute('data-title-left')
        : modal.getAttribute('data-title-right');
    if (title && titleText) title.textContent = titleText;

    modal.setAttribute('data-active-eye', eye);

    var trigger = getRxTrigger(instance, field, eye);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    modal._basariReturnFocus = document.activeElement;
    modal.removeAttribute('hidden');
    document.documentElement.classList.add(SPH_MODAL_BODY_CLASS);
    document.body.classList.add(SPH_MODAL_BODY_CLASS);

    var dialog = modal.querySelector('[data-basari-cl-rx-dialog]');
    window.setTimeout(function () {
      var selected = highlightRxModalValue(modal, getRxLiveValue(instance, field, eye));
      var focusNode = selected || dialog;
      if (focusNode && typeof focusNode.focus === 'function') focusNode.focus();
    }, 0);

    if (!modal._basariKeyHandler) {
      modal._basariKeyHandler = function (event) {
        if (modal.hasAttribute('hidden')) return;
        var key = event.key;
        if (key === 'Escape' || event.keyCode === 27) {
          event.preventDefault();
          closeRxSelectModal(instance, field);
          return;
        }
        if (key === 'Tab') {
          var focusables = getFocusable(dialog || modal);
          if (!focusables.length) return;
          var first = focusables[0];
          var last = focusables[focusables.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
          return;
        }
        var option =
          event.target && event.target.closest
            ? event.target.closest('[data-basari-cl-rx-option]')
            : null;
        if (!option || !modal.contains(option)) return;
        if (key === 'ArrowDown' || key === 'ArrowRight') {
          event.preventDefault();
          moveRxRadioFocus(modal, option, 1);
          return;
        }
        if (key === 'ArrowUp' || key === 'ArrowLeft') {
          event.preventDefault();
          moveRxRadioFocus(modal, option, -1);
          return;
        }
        if (key === 'Home') {
          event.preventDefault();
          moveRxRadioFocus(modal, option, -getRxRadioOptions(modal).indexOf(option));
          return;
        }
        if (key === 'End') {
          event.preventDefault();
          var radios = getRxRadioOptions(modal);
          moveRxRadioFocus(modal, option, radios.length - 1 - radios.indexOf(option));
        }
      };
      document.addEventListener('keydown', modal._basariKeyHandler, true);
    }
  }

  function buildAxisValues(min, max, step) {
    var values = [];
    var start = Number(min);
    var end = Number(max);
    var inc = Number(step) || 10;
    var n;
    if (!inc || isNaN(start) || isNaN(end)) return values;
    for (n = start; n <= end; n += inc) {
      values.push(String(n));
    }
    return values;
  }

  function loadToricSelectOptions(instance) {
    if (!isToricLens(instance)) return;
    var cfg = getToricConfig(instance);
    var cylModal = getRxSelectModal(instance, 'cyl');
    if (cylModal && cylModal.getAttribute('data-basari-cl-rx-ready') !== 'true') {
      populateRxList(cylModal.querySelector('[data-basari-cl-rx-list]'), cfg.cylValues);
      cylModal.setAttribute('data-basari-cl-rx-ready', 'true');
    }
    var axisModal = getRxSelectModal(instance, 'axis');
    if (axisModal && axisModal.getAttribute('data-basari-cl-rx-ready') !== 'true') {
      populateRxList(
        axisModal.querySelector('[data-basari-cl-rx-list]'),
        buildAxisValues(cfg.axisMin, cfg.axisMax, cfg.axisStep)
      );
      axisModal.setAttribute('data-basari-cl-rx-ready', 'true');
    }
  }

  function loadMultifocalSelectOptions(instance) {
    if (!isMultifocalLens(instance)) return;
    var modal = getRxSelectModal(instance, 'add');
    if (!modal || modal.getAttribute('data-basari-cl-rx-ready') === 'true') return;
    populateRxList(
      modal.querySelector('[data-basari-cl-rx-list]'),
      getMultifocalConfig(instance).addBands
    );
    modal.setAttribute('data-basari-cl-rx-ready', 'true');
  }

  function loadRxOptions(instance) {
    loadSphOptions(instance);
    loadToricSelectOptions(instance);
    loadMultifocalSelectOptions(instance);
  }

  function onContactLensFormSubmit(instance, event) {
    if (!prepareContactLensPurchase(instance)) {
      event.preventDefault();
      if (typeof event.stopPropagation === 'function') event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    }
  }

  extensions.loadSphOptions = loadSphOptions;
  extensions.loadRxOptions = loadRxOptions;
  extensions.validate = validateContactLensPrescription;
  extensions.buyNow = buyNowContactLens;
  extensions.addToCart = addContactLensToCart;
  extensions.buildLineItemProperties = syncPrescriptionForPurchase;

  function bind(instance) {
    var dom = instance.dom;
    var i;

    for (i = 0; i < dom.packCards.length; i += 1) {
      dom.packCards[i].addEventListener('click', function (event) {
        selectPack(instance, event.currentTarget);
      });
    }

    for (i = 0; i < dom.powerTabs.length; i += 1) {
      dom.powerTabs[i].addEventListener('click', function (event) {
        var mode = event.currentTarget.getAttribute('data-power-mode');
        setPowerMode(instance, mode);
      });
    }

    for (i = 0; i < dom.eyeInputs.length; i += 1) {
      dom.eyeInputs[i].addEventListener('click', function (event) {
        preventLastEyeUncheck(instance, event);
      });
      dom.eyeInputs[i].addEventListener('change', function (event) {
        var input = event.currentTarget;
        if (!input.checked && countCheckedEyes(instance) < 1) {
          input.checked = true;
          return;
        }
        var eye = input.getAttribute('data-eye');
        setEyeVisibility(instance, eye, input.checked);
      });
    }

    for (i = 0; i < dom.rxFieldInputs.length; i += 1) {
      dom.rxFieldInputs[i].addEventListener('change', function (event) {
        var field = event.currentTarget.getAttribute('data-field');
        var detail = {
          field: field,
          eye: event.currentTarget.getAttribute('data-eye'),
          value: event.currentTarget.value,
          sectionId: instance.config.sectionId
        };
        dispatch(instance.root, 'basari:cl:rx-change', detail);
        if (field === 'sph') {
          dispatch(instance.root, 'basari:cl:sph-change', detail);
        }
        if (field === 'boxes') {
          dispatch(instance.root, 'basari:cl:boxes-change', detail);
          syncShopifyQuantity(instance);
        }
        if (field === 'cyl' && isToricLens(instance)) {
          syncAxisLockForEye(
            instance,
            detail.eye,
            isWithPower(instance) && isEyeChecked(instance, detail.eye),
            { cylChanged: true }
          );
        }
      });
    }

    for (i = 0; i < dom.sphTriggers.length; i += 1) {
      dom.sphTriggers[i].addEventListener('click', function (event) {
        var eye = event.currentTarget.getAttribute('data-eye');
        openSphModal(instance, eye);
      });
    }

    if (dom.rxTriggers) {
      for (i = 0; i < dom.rxTriggers.length; i += 1) {
        dom.rxTriggers[i].addEventListener('click', function (event) {
          openRxSelectModal(
            instance,
            event.currentTarget.getAttribute('data-field'),
            event.currentTarget.getAttribute('data-eye')
          );
        });
      }
    }

    bindSpecialTextInputs(instance);

    if (dom.form) {
      dom.form.addEventListener(
        'submit',
        function (event) {
          onContactLensFormSubmit(instance, event);
        },
        true
      );
    }

    if (dom.buyNow) {
      dom.buyNow.addEventListener('click', function () {
        dispatch(instance.root, 'basari:cl:buy-now', {
          sectionId: instance.config.sectionId
        });
        if (typeof extensions.validate === 'function' && !extensions.validate(instance)) {
          return;
        }
        if (typeof extensions.buyNow === 'function') {
          extensions.buyNow(instance);
          return;
        }
        if (typeof extensions.addToCart === 'function') {
          extensions.addToCart(instance, { intent: 'buy-now' });
        }
      });
    }

    if (dom.addToCart && !dom.addToCart.hasAttribute('data-basari-frame-cart')) {
      dom.addToCart.setAttribute('data-basari-cl-add-cart', '');
      if (document.querySelector('cart-drawer')) {
        dom.addToCart.setAttribute('aria-haspopup', 'dialog');
      }
      dom.addToCart.addEventListener('click', function (event) {
        if (event && typeof event.preventDefault === 'function') event.preventDefault();
        dispatch(instance.root, 'basari:cl:add-to-cart', {
          sectionId: instance.config.sectionId,
          intent: 'add-to-cart'
        });
        if (typeof extensions.addToCart === 'function') {
          extensions.addToCart(instance, { intent: 'add-to-cart' });
        }
      });
    }

    if (dom.subscribeOpen) {
      dom.subscribeOpen.addEventListener('click', function () {
        dispatch(instance.root, 'basari:cl:subscribe-open', {
          sectionId: instance.config.sectionId
        });
        if (typeof extensions.validate === 'function' && !extensions.validate(instance)) {
          return;
        }
        if (typeof extensions.openSubscribeModal === 'function') {
          extensions.openSubscribeModal(instance);
        }
      });
    }

    /* Keep CL form + open subscribe modal in sync with colour/variant changes. */
    instance.root.addEventListener('basari:variant:change', function (event) {
      var variant = event && event.detail ? event.detail.variant : null;
      var variantId = variant && variant.id != null ? variant.id : null;
      if (variantId != null) {
        syncVariantFormInput(instance, variantId);
        instance.root.setAttribute('data-variant-id', String(variantId));
      }
      var openModal = getSubscribeModal(instance);
      if (openModal && !openModal.hasAttribute('hidden')) {
        syncSubscribeProductState(instance, openModal);
        syncSubscribeSummary(instance, openModal);
      }
    });

    if (dom.deliveryZip) {
      dom.deliveryZip.addEventListener('change', function (event) {
        dispatch(instance.root, 'basari:cl:delivery-zip', {
          zip: event.currentTarget.value,
          sectionId: instance.config.sectionId
        });
        if (typeof extensions.lookupDeliveryZip === 'function') {
          extensions.lookupDeliveryZip(instance, event.currentTarget.value);
        }
      });
    }

    if (dom.couponCopy) {
      dom.couponCopy.addEventListener('click', function () {
        copyCoupon(instance);
      });
    }
  }

  function BasariContactLensInstance(root) {
    this.root = root;
    this.config = parseConfig(root);
    this.dom = cacheDom(root);
    bind(this);
    syncEyeWarning(this);
    syncSphRowVisibility(this, this.dom.power && this.dom.power.getAttribute('data-hide-sph') === 'true');
    updateEyeFieldState(this);

    if (typeof extensions.loadRxOptions === 'function') {
      extensions.loadRxOptions(this);
    } else if (typeof extensions.loadSphOptions === 'function') {
      extensions.loadSphOptions(this);
    }
    if (typeof extensions.loadPacks === 'function') {
      extensions.loadPacks(this);
    }
    if (typeof extensions.checkDeliveryEligibility === 'function') {
      extensions.checkDeliveryEligibility(this);
    }

    dispatch(root, 'basari:cl:ready', {
      sectionId: this.config.sectionId,
      productId: this.config.productId
    });
  }

  function initRoot(root) {
    if (!root || root.getAttribute(READY_ATTR) === 'true') return;
    root.setAttribute(READY_ATTR, 'true');
    new BasariContactLensInstance(root);
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll
      ? (scope || document).querySelectorAll(ROOT_SELECTOR)
      : [];
    var i;
    for (i = 0; i < roots.length; i += 1) {
      initRoot(roots[i]);
    }
  }

  function onSectionLoad(event) {
    var section = event.target;
    if (!section || !section.querySelector) return;
    if (!section.querySelector(ROOT_SELECTOR)) return;
    initAll(section);
  }

  window.BasariContactLens = {
    init: initAll,
    extensions: extensions,
    discoverRechargePlans: discoverRechargePlans,
    version: '0.8.0-multifocal'
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', onSectionLoad);
})();
